import { create } from 'zustand';
import { coreApi } from '@/integrations/api/active';
import { ensurePlanningChecklists } from '@/modules/planning/ensure-checklists';
import { apiPost } from '@/integrations/api/client';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspaceStore } from '@/modules/workspaces/workspace-store';
import { workspaceHasFeature } from '@/modules/workspaces/workspace-features';
import { entitledMeetingTypeSources } from '@/modules/meetings/entitled-meeting-types';
import { applyWrite } from '@/shared/lib/store-write';
import { diffFields, descriptorsFor, resolveActor } from '@/modules/audit/audit-log';
import * as taskMutations from '@/modules/tasks/mutations';
import * as rockMutations from '@/modules/rocks/mutations';
import * as metricMutations from '@/modules/scorecards/mutations';
import { workspaceMetricThresholds } from '@/modules/workspaces/workspace-settings';
import * as issueMutations from '@/modules/issues/mutations';
import * as milestoneMutations from '@/modules/rocks/milestone-mutations';
import type { MutationContext } from '@/modules/activity/mutation-context';
import { differenceInCalendarDays } from 'date-fns';
import { dateToLocalStr, parseDateStr } from '@/shared/lib/date-utils';
import { pickBadgeColor, isBadgeColor } from '@/modules/people/badge-colors';
import { useNotificationsStore } from '@/modules/notifications/notifications-store';
import { useStakeholderStore } from '@/modules/stakeholders/stakeholder-store';
import { useTeamsStore } from '@/modules/teams/teams-store';
import { ancestorChain } from '@/modules/teams/types';
import { getFramework } from '@/modules/stakeholders/stakeholder-frameworks';
import { getQuarterStartDateStr, DEFAULT_PERIOD_CONFIG } from '@/modules/cycles/period-utils';
import { inCycle } from '@/modules/cycles/cycle-utils';
import { storeTerms } from '@/hooks/useTerms';
import {
  resolveVision as resolveVisionPure, visionSpan as visionSpanPure, clearOneYearKeys,
  isOpenVision as isOpenVisionPure, visionDecisionsOf as decisions,
} from '@/modules/vision/vision-resolver';
import { storeNotice } from '@/shared/lib/store-notice';
import { cycleIsDraft, cycleKey, cycleLabel, isCycleWritable, mergeAvailableQuarters, resolveDisplayedCycle } from '@/modules/cycles/cycle-state';
import type { Cycle, CycleState, CycleVision } from '@/modules/cycles/types';
import type { ChangedRecords } from '@/modules/meetings/meeting-notices';
import { meetingFromRow, meetingScheduleFromRow, meetingScheduleToRow } from '@/modules/meetings/row-mappers';
import {
  movedOccurrencesAhead, planSeriesPropagation, type FieldPropagation,
} from '@/modules/meetings/series-propagation';
import { buildRRule, parseRRule } from '@/modules/meetings/meeting-schedules';
import type { AnnualGoal, Rock, Milestone, MilestoneDependency, RockUpdate } from '@/modules/rocks/types';
import type { Metric, MetricEntry, Scorecard, ScorecardMetric } from '@/modules/scorecards/types';
import type { Task, TaskQueuedAction } from '@/modules/tasks/types';
import type { Issue, IssueQueuedAction } from '@/modules/issues/types';
import type { Person } from '@/modules/people/types';
import type { Meeting, MeetingAttendee, MeetingSchedule, MeetingStatus, AgendaItem, MeetingSectionTargets } from '@/modules/meetings/types';
import type { PlanningChecklist, PlanningChecklistItem, PlanningSession, RockDecision, PlanningStep, RockDecisionKind, PlanningSessionKind } from '@/modules/planning/types';
import type { Vision } from '@/modules/vision/types';
import type { Idea, RockIdeaSourceMilestone, IdeaStatus } from '@/modules/ideas/types';
import { milestoneDependencyFromRow, milestoneFromRow, rockFromRow, rockUpdateFromRow } from '@/modules/rocks/row-mappers';
import { metricFromRow, scorecardFromRow, METRIC_SELECT } from '@/modules/scorecards/row-mappers';
import { keepsCatalogKey } from '@/modules/scorecards/metric-catalog';
import { taskFromRow } from '@/modules/tasks/row-mappers';
import { withInsertedTask } from '@/modules/tasks/live-tasks';
import { issueFromRow } from '@/modules/issues/row-mappers';
import { planningChecklistFromRow, planningSessionFromRow } from '@/modules/planning/row-mappers';
import { visionFromRow, visionToRow } from '@/modules/vision/row-mappers';
import { serializeFathomSummaries, serializeFathomSummary } from '@/modules/meetings/fathom-summary-rows';

/**
 * Use the same team for an optimistic entity and its insert payload; otherwise
 * the optimistic row is temporarily scoped to the root.
 */
function newEntityTeamId(state: Pick<OperatingState, 'currentTeamId' | 'rootTeamId'>): string | undefined {
  return state.currentTeamId ?? state.rootTeamId ?? undefined;
}

function metricInsertRow(metric: Metric, workspaceId: string | null) {
  return {
    team_id: metric.teamId ?? null,
    id: metric.id, title: metric.title,
    unit: metric.unit ?? null,
    decimals: metric.decimals ?? null,
    owner_id: metric.ownerId ?? null,
    goal_ids: metric.goalIds,
    origin: metric.origin,
    formula_expression: metric.formulaExpression ?? null,
    aggregate_source_metric_id: metric.aggregate?.sourceMetricId ?? null,
    aggregate_function: metric.aggregate?.function ?? null,
    aggregate_trailing_periods: metric.aggregate?.trailingPeriods ?? null,
    aggregate_incomplete_policy: metric.aggregate?.incompletePolicy ?? null,
    start_period: metric.startPeriod ?? null,
    opening_value: metric.openingValue ?? null,
    catalog_key: metric.catalogKey ?? null,
    cadence: metric.cadence ?? 'weekly',
    indicator: metric.indicator ?? null,
    rock_ids: metric.rockIds ?? [],
    targets: metric.targets ?? [],
    workspace_id: workspaceId,
  };
}

/** Scope a child table through its parent; avoid invalid empty `id.in.()` filters. */
function scopedVia<T>(q: T, parent: string, here: string | null, sharedParentIds: string[]): T {
  if (!here) return q;
  const anyQ = q as any;
  return sharedParentIds.length
    ? anyQ.or(`team_id.eq.${here},id.in.(${sharedParentIds.join(',')})`, { referencedTable: parent })
    : anyQ.eq(`${parent}.team_id`, here);
}

function scratchVision(workspaceId: string): Vision {
  const structure = useWorkspaceStore.getState().settings?.visionTemplate ?? null;
  const now = new Date().toISOString();
  return { id: 'scratch', workspaceId, name: 'Untitled', structure, data: {}, createdAt: now, updatedAt: now };
}

// Module-level ref for the Realtime channel (avoids storing non-serializable objects in Zustand state)
let _operatingChannel: ReturnType<typeof supabase.channel> | null = null;
let _operatingDataRequestGeneration = 0;
let _operatingSubscriptionGeneration = 0;

/** In a solo (single-member) workspace, default owner to the single linked Person. */
function resolvePersonalOwnerId(provided: string | null | undefined, people: Person[]): string | null {
  if (provided) return provided;
  const ws = useWorkspaceStore.getState();
  if (!ws.currentWorkspace || (ws.currentWorkspace.purchasedSeats ?? 1) > 1) {
    return provided ?? null;
  }
  const me = ws.members.find((m) => m.userId && m.personId);
  if (me?.personId) return me.personId;
  return people[0]?.id ?? null;
}

/** Store-level cycle backstop for stale tabs or an omitted UI gate. */
function blockedByCycle(
  state: Pick<OperatingState, 'cycles' | 'unlockedCycleKey'>,
  year: number | null | undefined,
  quarter: number | null | undefined,
): boolean {
  const myRole = useWorkspaceStore.getState().myRole;
  const canManage = myRole === 'owner' || myRole === 'admin';
  const writable = isCycleWritable({
    cycles: state.cycles,
    year,
    quarter,
    canManage,
    unlockedKey: state.unlockedCycleKey,
  });
  if (writable) return false;
  storeNotice.error(`${cycleLabel(year, quarter)} is closed`, {
    description: canManage
      ? 'Unlock the cycle from the bar at the top to edit it.'
      : 'Ask a workspace admin to reopen it.',
  });
  return true;
}

/** Milestones have no cycle of their own — they follow their parent rock's. */
function milestoneCycleBlocked(
  state: Pick<OperatingState, 'cycles' | 'unlockedCycleKey' | 'rocks'>,
  rockId: string,
): boolean {
  const parent = state.rocks.find((r) => r.id === rockId);
  if (!parent) return false;
  return blockedByCycle(state, parent.year, parent.quarter);
}

/** A milestone's home is its rock's, read from a write that selected `rocks(team_id)`. */
function milestoneTeamId(rows: unknown[] | null): string | undefined {
  return (rows?.[0] as { rocks: { team_id: string } } | undefined)?.rocks.team_id;
}

/** Shift a YYYY-MM-DD string by whole days, matching the cascade's arithmetic. */
function shiftDateStr(dateStr: string, days: number): string {
  const d = parseDateStr(dateStr);
  d.setDate(d.getDate() + days);
  return dateToLocalStr(d);
}


/**
 * Announce a completed milestone to the people accountable for it.
 *
 * Two representations of milestone accountability coexist until they are
 * reconciled (#251): the first-class `owner_id`, and the owner-role holder in
 * the rock's stakeholder matrix. Both are notified, along with the rock's owner
 * and the rock's followers; `triggerNotification` dedupes.
 */
/**
 * Write a cycle's state, creating the row if this workspace never had one.
 * Upsert rather than update because cycles are a sidecar added late: a quarter
 * can exist in the data (and in `availableQuarters`) without ever having had a
 * row, and activating it shouldn't silently do nothing.
 */
async function upsertCycleRow(
  workspaceId: string,
  year: number,
  quarter: number | null,
  state: CycleState,
): Promise<string | null> {
  const { data, error } = await coreApi(`/workspaces/${workspaceId}/cycles/state`, { year, quarter, state });
  if (error) {
    console.error('[upsertCycleRow] failed:', error.message);
    return null;
  }
  return (data?.[0] as { id?: string } | undefined)?.id ?? null;
}

/** Optimistic mirror of `upsertCycleRow`. `id` must be the persisted one — `cycle_visions.cycle_id` FKs to it. */
function withCycleState(
  cycles: Cycle[],
  workspaceId: string,
  year: number,
  quarter: number | null,
  state: CycleState,
  stampActivated: boolean,
  id?: string | null,
): Cycle[] {
  const key = cycleKey(year, quarter);
  const activatedAt = stampActivated ? new Date().toISOString() : undefined;
  const existing = cycles.find((c) => cycleKey(c.year, c.quarter) === key);
  if (existing) {
    return cycles.map((c) => (cycleKey(c.year, c.quarter) === key
      ? { ...c, state, ...(activatedAt ? { activatedAt } : {}) }
      : c));
  }
  return [...cycles, { id: id ?? crypto.randomUUID(), workspaceId, year, quarter, state, activatedAt }];
}

interface OperatingState {
  loading: boolean;
  workspaceId: string | null;
  /**
   * The team `fetchAll` last ran for. Kept because a recovery refetch has to
   * reproduce the scope it is repairing — refetching with no team would
   * quietly swap the loaded slice for the root's.
   */
  fetchedTeamId: string | null;

  /** Available years and year+quarter combos derived from entity data. */
  availableYears: number[];
  availableQuarters: { year: number; quarter: number }[];
  /** Displayed cycle and new-item target; writability is resolved separately. */
  currentYear: number | null;
  currentQuarter: number | null;
  /**
   * The workspace's *primary* cycle, persisted on
   * `workspaces.active_year` / `active_quarter`. The default view and the
   * default write target. Always one of the open cycles.
   */
  activeYear: number | null;
  activeQuarter: number | null;
  /** Per-cycle state — planned/open/closed. See src/modules/cycles/types.ts. */
  cycles: Cycle[];
  /** Vision revision points, one per (cycle, team). See src/modules/cycles/types.ts. */
  cycleVisions: CycleVision[];
  /** The workspace's unique root team. */
  rootTeamId: string | null;
  /**
   * Team new entities belong to, mirrored from the resolved route because the
   * store cannot read the URL. Database defaults otherwise select the root.
   */
  currentTeamId: string | null;
  setCurrentTeamId: (id: string | null) => void;
  /**
   * The one closed cycle a manager has unlocked for editing, as a `cycleKey`.
   * Session-scoped on purpose: it dies on refresh, and on navigating to any
   * other cycle, so an unlock can never linger as state somebody has to
   * remember to clear — or, worse, quietly authorise an edit two quarters later.
   */
  unlockedCycleKey: string | null;
  /**
   * The one rock whose baselines a manager has unlocked, so date edits move the
   * agreed plan with them instead of registering as slip.
   *
   * Session-scoped for the same reasons as `unlockedCycleKey`, and one at a
   * time: re-agreeing a plan is a deliberate act, not a mode the app sits in.
   */
  unlockedBaselineRockId: string | null;
  /** Unlock one closed cycle for this session — dies on refresh and on navigating away. */
  setUnlockedCycleKey: (key: string | null) => void;
  /**
   * Unlock a rock's baselines, or pass null to re-lock. Unlocking is recorded
   * in the audit log: the baseline is what makes slip answerable, so re-agreeing
   * one has to leave a trace of who decided to.
   */
  setUnlockedBaselineRockId: (rockId: string | null) => void;
  /**
   * Whether the cycle bar is showing. Lives in the store rather than in the
   * component because the URL mirrors it: `?year=&quarter=` is present exactly
   * when the bar is, so a copied link reproduces what the sender is looking at.
   */
  cycleBarOpen: boolean;

  goals: AnnualGoal[];
  rocks: Rock[];
  milestones: Milestone[];
  milestoneDependencies: MilestoneDependency[];
  metrics: Metric[];
  scorecards: Scorecard[];
  scorecardMetrics: ScorecardMetric[];
  tasks: Task[];
  issues: Issue[];
  people: Person[];
  meetings: Meeting[];
  /** The recurring series for this team. Occurrences are materialized into `meetings`. */
  meetingSchedules: MeetingSchedule[];
  /** IDs of meetings currently being processed by analyze-meeting. */
  analyzingMeetingIds: string[];
  /** IDs of meetings currently having a recap generated. */
  generatingRecapIds: string[];
  planningChecklists: PlanningChecklist[];
  visions: Vision[];

  ideas: Idea[];
  planningSessions: PlanningSession[];
  rockDecisions: RockDecision[];

  /** Home-only cross-team work, populated by `fetchMyItems`. */
  myRocks: Rock[];
  myTasks: Task[];
  myIssues: Issue[];
  myMetrics: Metric[];
  /** Milestones of `myRocks` only — home draws progress rings, which need them. */
  myMilestones: Milestone[];
  /** Updates on `myRocks` only — home's celebration moment reads them (lib/home-moments). */
  myRockUpdates: RockUpdate[];

  crossTeamRocks: Rock[];
  crossTeamTasks: Task[];
  crossTeamIssues: Issue[];
  crossTeamMetrics: Metric[];
  crossTeamScorecardMetricIds: string[];
  crossTeamReady: boolean;

  /** Load one workspace and team; cross-team callers use dedicated queries. */
  fetchAll: (workspaceId: string, teamId?: string | null) => Promise<void>;
  /** Drop every domain collection — on a team change and on an account change. */
  resetDomain: () => void;
  resetSessionView: () => void;
  /** Fill the `my*` slices from `my_items()`. Home's fetch; nothing else calls it. */
  fetchMyItems: (workspaceId: string) => Promise<void>;
  fetchCrossTeamTotals: (workspaceId: string) => Promise<void>;

  /** Switch displayed year/quarter (local only, no DB write). */
  setCurrentYear: (year: number) => void;
  setCurrentQuarter: (year: number, quarter: number) => void;
  /**
   * Open a cycle AND make it the workspace's primary. This is Planning's
   * "Activate" action, and it doubles as the recovery path when a quarter was
   * activated prematurely — activating the earlier one moves the pointer back.
   */
  activateCycle: (year: number, quarter: number | null, opts?: { closeOthers?: boolean }) => Promise<void>;
  /**
   * Open a cycle WITHOUT moving the primary. The boundary case (keep last
   * quarter open while the new one leads) and reopening something historical.
   */
  reopenCycle: (year: number, quarter: number | null) => Promise<void>;
  /** Close a cycle. Refuses on the primary — activate another one first. */
  closeCycle: (year: number, quarter: number | null) => Promise<void>;
  /** Session-scoped unlock of a single closed cycle. Pass null to re-lock. */
  setUnlockedCycle: (key: string | null) => void;
  /** Show/hide the cycle bar. Hiding also returns to the primary cycle. */
  setCycleBarOpen: (open: boolean) => void;
  /** Persist active year to workspaces table. Admin/manager only — enforce in UI. */
  setActiveYear: (year: number) => Promise<void>;
  /** Register a new year (updates available list) or new quarter (updates available list; may branch the vision). */
  ensureYear: (year: number) => Promise<void>;
  ensureQuarter: (year: number, quarter: number) => Promise<void>;

  addGoal: (goal: AnnualGoal) => void;
  updateGoal: (id: string, updates: Partial<AnnualGoal>) => void;
  deleteGoal: (id: string) => void;

  addRock: (rock: Rock) => Promise<boolean>;
  updateRock: (id: string, updates: Partial<Rock>, opts?: { skipStatusRecord?: boolean }) => Promise<boolean>;
  deleteRock: (id: string) => Promise<boolean>;

  addMilestone: (milestone: Milestone) => Promise<void>;
  updateMilestone: (id: string, updates: Partial<Pick<Milestone, 'title' | 'completed' | 'startDate' | 'endDate' | 'ownerId' | 'weight' | 'position'>>) => Promise<void>;
  /**
   * Move a milestone's dates, shifting everything downstream by the same delta.
   * Returns what actually moved and what was refused, from the database rather
   * than from the client's guess.
   */
  rescheduleMilestone: (
    id: string, startDate: string | undefined, endDate: string | undefined,
    opts?: { cascade?: boolean },
  ) => Promise<{ moved: { id: string; title: string; ownerId?: string }[]; skipped: { id: string; title: string }[] } | null>;
  addMilestoneDependency: (
    predecessorId: string, successorId: string,
    opts?: { lagDays?: number; type?: MilestoneDependency['type'] },
  ) => Promise<void>;
  /**
   * Edit an edge's type or lag. Direction is deliberately absent: both ends'
   * dates were arranged under the old direction, so flipping in place would
   * silently invalidate the arrangement. Removing and redrawing restates it.
   */
  updateMilestoneDependency: (
    id: string, patch: { lagDays?: number; type?: MilestoneDependency['type'] },
  ) => Promise<void>;
  deleteMilestoneDependency: (id: string) => Promise<void>;
  deleteMilestone: (id: string) => Promise<void>;
  toggleMilestone: (id: string) => Promise<void>;

  rockUpdates: RockUpdate[];
  addRockUpdate: (update: Omit<RockUpdate, 'id' | 'workspaceId' | 'authorId' | 'authorMemberId' | 'createdAt'>) => Promise<void>;
  editRockUpdate: (id: string, message: string) => Promise<void>;
  deleteRockUpdate: (id: string) => Promise<void>;

  addMetric: (metric: Metric) => Promise<boolean>;
  addCatalogMetrics: (plan: { create: Metric[]; links: { metric: Metric; catalogKey: string }[] }) => Promise<boolean>;
  updateMetric: (id: string, updates: Partial<Metric>) => void;
  deleteMetric: (id: string) => Promise<void>;
  addMetricEntry: (metricId: string, period: string, value: number | null) => void;

  /** Resolves once the row is written, so a caller can insert something that references it. */
  addScorecard: (scorecard: Scorecard) => Promise<boolean>;
  updateScorecard: (id: string, updates: Partial<Pick<Scorecard, 'name'>>) => void;
  archiveScorecard: (id: string) => Promise<void>;
  restoreScorecard: (id: string) => Promise<void>;
  /** Permanent. The database refuses a board that is not archived first. */
  deleteScorecard: (id: string) => Promise<void>;
  cloneScorecard: (sourceId: string, newName: string) => string;

  addScorecardMetric: (qsk: ScorecardMetric) => Promise<boolean>;
  removeScorecardMetric: (id: string) => void;

  addTask: (item: Task) => void;
  updateTask: (id: string, updates: Partial<Task>) => void;
  deleteTask: (id: string) => void;
  queueTaskAction: (id: string, action: TaskQueuedAction | null) => void;

  addIssue: (issue: Issue) => void;
  updateIssue: (id: string, updates: Partial<Issue>) => void;
  deleteIssue: (id: string) => void;
  queueIssueAction: (id: string, action: IssueQueuedAction | null) => void;
  commitQueuedStates: () => Promise<void>;

  addPerson: (member: Person) => Promise<boolean>;
  updatePerson: (id: string, updates: Partial<Person>) => Promise<boolean>;
  removePerson: (id: string) => Promise<boolean>;

  // Meetings
  addMeeting: (meeting: Meeting) => void;
  /**
   * `audit` records a field edit a person made to this meeting. A change of
   * status is recorded either way; a row rewritten by a series edit is not.
   */
  updateMeeting: (id: string, updates: Partial<Meeting>, opts?: { audit?: boolean; changed?: ChangedRecords }) => void;
  updateMeetingQuarterlySetup: (id: string, setup: Record<string, unknown>) => Promise<void>;
  markMeetingPrepSent: (id: string) => void;
  deleteMeeting: (id: string) => void;

  // Recurring series (#411)
  addMeetingSchedule: (schedule: MeetingSchedule) => Promise<boolean>;
  /**
   * Rewrites the rule in place and propagates to its undiverged future
   * occurrences. "All meetings" in the scope prompt; there is no split.
   */
  updateMeetingSchedule: (
    id: string,
    updates: Partial<MeetingSchedule>,
    /** The meetings that take the series' new host, roster or prep with it. */
    opts?: { carry?: FieldPropagation[] },
  ) => Promise<boolean>;
  /**
   * Ends the series: no occurrence after now, materialized history untouched.
   * `moved` decides the future occurrences someone moved on their own.
   */
  endMeetingSchedule: (id: string, moved?: 'keep' | 'delete') => Promise<boolean>;
  /**
   * Bring this workspace's occurrences up to date now, rather than at the top
   * of the next hour, and merge whatever appeared into the list.
   */
  materializeSchedules: () => Promise<void>;

  // Draft meetings
  saveMeetingDraft: (meeting: Meeting, draftState: import('@/modules/meetings/types').MeetingDraftState) => Promise<boolean>;
  deleteMeetingDraft: (id: string) => Promise<void>;
  returnMeetingToScheduled: (scheduled: Meeting) => void;

  // Fathom AI summaries on weekly meetings
  attachFathomSummary: (meetingId: string, payload: { url: string; summary?: string; candidateTasks?: import('@/modules/meetings/types').FathomCandidateTask[]; inboxRowId?: string }) => Promise<void>;
  /** Call the fetch-fathom-meeting edge function to pull summary + candidate tasks from Fathom API, then attach. */
  fetchAndAttachFathomByUrl: (meetingId: string, shareUrl: string) => Promise<void>;
  fetchFathomInboxCandidates: (meetingId: string) => Promise<{ id: string; url: string; summary?: string; meetingTitle?: string; recordedAt: string; candidateTasks: import('@/modules/meetings/types').FathomCandidateTask[] }[]>;
  decideFathomCandidate: (
    meetingId: string,
    candidateId: string,
    decision: { type: 'accept'; ownerId?: string; dueDate?: string }
            | { type: 'replace' }
            | { type: 'reject' }
            | { type: 'unset' }
  ) => Promise<void>;
  /** After a meeting ends, look for an unattached Fathom webhook inbox row whose recorded_at is near
   *  the meeting date and (optionally) whose meeting_title matches workspace_settings.fathom_meeting_title.
   *  If found, append it to fathomSummaries (subject to sessionCount cap). */
  tryMatchFathomInboxForMeeting: (meetingId: string) => Promise<boolean>;
  /** Invoke the analyze-meeting edge function to generate IDS breakdowns and meeting notes from Fathom. */
  analyzeMeeting: (meetingId: string) => Promise<void>;
  /** Invoke the generate-meeting-recap edge function and save the result to the meeting record. */
  generateMeetingRecap: (meetingId: string) => Promise<void>;
  /** Persist an edited recap draft (debounced, no audit log). */
  saveMeetingRecap: (meetingId: string, recap: string) => Promise<void>;
  /** Send the recap to a list of recipients via send-transactional-email. */
  sendMeetingRecap: (meetingId: string, recap: string, recipients: { name?: string; email: string }[]) => Promise<{ sent: number; failed: number }>;

  // Planning Checklists
  addPlanningChecklist: (checklist: PlanningChecklist) => Promise<void>;
  updatePlanningChecklist: (id: string, items: PlanningChecklistItem[]) => void;

  // Vision
  /** The vision in force for a cycle — backward scan for the nearest revision point. Never returns nothing. */
  /**
   * The vision in force for a cycle, on a team. `teamId` is REQUIRED — see the
   * implementation for why it is not defaulted to the current team.
   */
  resolveVision: (year: number | null, quarter: number | null, teamId: string | null) => Vision;
  visionSpan: (visionId: string) => import('@/modules/vision/vision-resolver').VisionSpan;
  /** True iff this vision is the one that resolves for the workspace's actual active cycle — the only vision that can be ratified. */
  isOpenVision: (visionId: string) => boolean;
  /** Branches the vision for a newly-created year — the yearly-revision rhythm. Internal; called from ensureQuarter. */
  branchVisionForYear: (year: number, quarter: number) => Promise<void>;
  /** All visions, most recently created first. */
  listVisions: () => Vision[];
  createVision: (name?: string, teamId?: string | null) => Promise<Vision>;
  renameVision: (id: string, name: string) => Promise<void>;
  /** Unattached copy — stays in the list, creates no decision. */
  cloneVision: (id: string, targetTeamId?: string | null) => Promise<Vision>;
  deleteVision: (id: string) => Promise<void>;
  /** Rewrites content in place (amends history). Gated by the origin cycle's writability. */
  saveVisionInPlace: (id: string, patch: { data?: import('@/modules/vision/template-types').VisionData; structure?: import('@/modules/vision/template-types').VisionTemplate }) => Promise<void>;
  /** Confirms a vision as still current without editing it. */
  ratifyVision: (id: string) => Promise<void>;
  /** Creates a new vision and attaches it as the decision for a cycle. */
  saveVisionAsNew: (
    fromId: string,
    name: string,
    patch: { data?: import('@/modules/vision/template-types').VisionData; structure?: import('@/modules/vision/template-types').VisionTemplate },
    /**
     * Where the new document attaches. `teamId` DEFAULTS TO THE SOURCE
     * DOCUMENT'S SPACE, never the current one (§ 5.2, #150) — revise means
     * "revise *this* document", so forking the company vision onto a team-local
     * one has to be a deliberate act rather than a consequence of which page
     * someone happened to be on.
     */
    attachAt?: { year: number; quarter: number | null; teamId?: string | null },
  ) => Promise<Vision>;
  updateVisionTemplate: (id: string, structure: import('@/modules/vision/template-types').VisionTemplate) => Promise<void>;
  /**
   * Low-level setter for a team's per-cycle vision decision.
   * - `visionId: string` → attach that vision as the decision for this (cycle, team)
   * - `visionId: 'inherit'` → write a null-vision_id tombstone (explicit choice to inherit)
   * - `visionId: null` → delete the row (clear any existing decision)
   */
  setVisionDecision: (
    year: number, quarter: number | null, visionId: string | null | 'inherit',
    /** Defaults to the vision's own team; see the implementation. */
    teamId?: string | null,
  ) => Promise<void>;
  /** Makes `visionId` the root team's decision at this cycle, unless the root already resolves a vision there. */
  ensureRootVisionDecision: (year: number, quarter: number | null, visionId: string) => Promise<void>;
  /** Moves a vision's decision to a new cycle — clears the old one first. */
  moveVisionDecision: (visionId: string, target: { year: number; quarter: number | null }) => Promise<void>;
  /** Creates a blank vision homed in `teamId` and immediately sets it as the decision for the cycle — the "Stop Inheriting" write. */
  stopInheriting: (year: number, quarter: number | null, teamId: string) => Promise<Vision | null>;
  /** Detaches a vision from its effective cycle, converting it to a draft. Returns the quarters whose resolution changes. */
  detachVision: (visionId: string) => Promise<{ year: number; quarter: number | null }[]>;

  // Ideas (unified parking lot)
  addIdea: (idea: Partial<Idea> & { title: string }) => Promise<Idea>;
  updateIdea: (id: string, updates: Partial<Idea>) => Promise<void>;
  deleteIdea: (id: string) => Promise<void>;
  combineIdeas: (sourceIds: string[], targetId: string) => Promise<void>;
  promoteIdeaToRock: (ideaId: string, sessionId: string | null, rockPatch: Partial<Rock> & { id?: string; year: number; quarter: number; title: string }) => Promise<Rock>;

  // Planning sessions
  /** The planning run for a cycle on a team, created on first reach. Idempotent. */
  ensurePlanningSession: (args: {
    kind: PlanningSessionKind;
    year?: number;
    quarter?: number;
    prevYear?: number;
    prevQuarter?: number;
    facilitatorId?: string;
    /** Defaults to the current scope. A docked meeting supplies its own. */
    teamId?: string | null;
  }) => Promise<PlanningSession>;
  recordDecision: (args: Omit<RockDecision, 'id' | 'decidedAt' | 'workspaceId'>) => Promise<RockDecision>;
  /** Inserts an idea seeded from a prior-quarter rock. Idempotent: skips if a non-terminal idea for this rock already exists. */
  seedCarryForwardIdea: (args: { sessionId: string; rock: Rock; targetQuarter?: string }) => Promise<Idea | null>;
  promoteGoalIdeaToGoal: (ideaId: string, sessionId: string, goalPatch: Partial<AnnualGoal> & { id?: string; year: number; title: string }) => Promise<AnnualGoal>;
  completePlanningSession: (sessionId: string, notes?: string) => Promise<void>;

  subscribeToRealtime: (workspaceId: string) => void;
  unsubscribeFromRealtime: () => void;

  /** Toggle-OFF path: null out year/quarter on all items. If inferDueDates, infer dueDate for rocks lacking one. */
  nullifyItemCycles: (workspaceId: string, inferDueDates: boolean) => Promise<void>;
  /** Toggle-ON "capture" path: assign year/quarter to all open cycle-less items. */
  assignCyclesToCyclelessItems: (workspaceId: string, year: number, quarter: number) => Promise<void>;
}

/**
 * Re-read the domain in the scope it was last fetched with. For recovery only:
 * it costs a full round trip, so it is the fallback for mutations whose local
 * inverse is not worth hand-writing, never the routine path.
 */
function refetchDomain(get: () => OperatingState) {
  return () => {
    const { workspaceId, fetchedTeamId } = get();
    if (workspaceId) void get().fetchAll(workspaceId, fetchedTeamId);
  };
}

type OperatingSet = (fn: (s: OperatingState) => Partial<OperatingState>) => void;

const carriedRow = (updates: Partial<Meeting>): Record<string, unknown> => {
  const row: Record<string, unknown> = {};
  if (updates.hostId !== undefined) row.host_id = updates.hostId ?? null;
  if (updates.invitees !== undefined) row.invitees = updates.invitees;
  if (updates.prepEmailEnabled !== undefined) row.prep_email_enabled = updates.prepEmailEnabled ?? null;
  if (updates.notificationTimingHours !== undefined) {
    row.notification_timing_hours = updates.notificationTimingHours ?? null;
  }
  return row;
};

function commitSeries(
  get: () => OperatingState,
  set: OperatingSet,
  id: string,
  updates: Partial<MeetingSchedule>,
  opts: { carry?: FieldPropagation[]; moved?: 'keep' | 'delete' } = {},
): Promise<boolean> {
  const before = get().meetingSchedules.find((sc) => sc.id === id);
  if (!before) return Promise.resolve(false);
  const after = { ...before, ...updates };
  const meetingsBefore = get().meetings;

  const now = new Date().toISOString();
  const tz = useWorkspaceStore.getState().settings?.timezone ?? 'UTC';
  const movedGone = new Set(opts.moved === 'delete'
    ? movedOccurrencesAhead(id, meetingsBefore, now).map((m) => m.id) : []);
  const plan = planSeriesPropagation(after, meetingsBefore.filter((m) => !movedGone.has(m.id)), tz, now);
  const gone = new Set([...plan.remove, ...movedGone]);
  const rewritten = new Map(plan.rewrite.map((row) => [row.id, row]));
  const carried = new Map((opts.carry ?? []).map((row) => [row.id, row.updates]));
  const day = new Intl.DateTimeFormat('en-CA', { timeZone: tz });

  set((s) => ({
    meetingSchedules: s.meetingSchedules.map((sc) => sc.id === id ? after : sc),
    meetings: s.meetings.filter((m) => !gone.has(m.id)).map((m) => {
      const moved = rewritten.get(m.id);
      const fields = carried.get(m.id);
      if (!moved && !fields) return m;
      return {
        ...m,
        ...(moved ? {
          occurrenceStart: moved.occurrenceStart,
          meetingScheduleId: after.id,
          scheduledAt: moved.scheduledAt,
          date: day.format(new Date(moved.scheduledAt)),
        } : {}),
        ...fields,
      };
    }),
  }));
  return applyWrite(
    coreApi(`/records/meeting-schedules/${id}`, {
      ...meetingScheduleToRow(updates),
      propagate: true,
      ...(opts.carry ? { carry: opts.carry.map((row) => ({ id: row.id, ...carriedRow(row.updates) })) } : {}),
      ...(opts.moved ? { moved: opts.moved } : {}),
    }, 'PATCH'),
    {
      noun: 'this schedule',
      context: 'updateMeetingSchedule',
      onSuccess: () => {
        void get().materializeSchedules();
      },
      rollback: () => {
        set((st) => ({ meetingSchedules: st.meetingSchedules.map((sc) => sc.id === id ? before : sc) }));
        if (gone.size || rewritten.size || carried.size) refetchDomain(get)();
      },
    },
  );
}

/**
 * The active session acting in the active workspace, as a mutation context.
 *
 * There is deliberately no `audit` field. Omitting it is what tells `logAudit`
 * and `triggerNotification` to resolve the actor from the stores, which is the
 * behavior every existing surface relies on. A caller writing to a workspace
 * that is not the active one builds its own context and supplies it.
 */
function activeContext(get: () => OperatingState): MutationContext {
  return {
    client: supabase,
    api: (path, body, method) => apiPost(
      async () => (await supabase.auth.getSession()).data.session?.access_token ?? null,
      path,
      body,
      method,
    ),
    unlockedCycleKey: () => get().unlockedCycleKey,
    workspaceId: get().workspaceId,
    isBlocked: (year, quarter) => blockedByCycle(get(), year, quarter),
    followersOf: (entityType, entityId) =>
      useNotificationsStore.getState().getFollowersOf(entityType, entityId),
    refetch: refetchDomain(get),
    defaultTeamId: () => newEntityTeamId(get()),
    resolveOwnerId: (ownerId) => resolvePersonalOwnerId(ownerId, get().people) ?? undefined,
  };
}

/**
 * Transitional server-backed operating-data facade, keyed by workspace and
 * fetched team. It remains one store while `fetchAll`, realtime replacement,
 * cycle transitions, and queued meeting/planning commits span domain slices.
 * Domain types, pure rules, and independently safe mutations live with their
 * modules; new responsibilities must not be added here.
 */
export const useOperatingStore = create<OperatingState>((set, get) => ({
  loading: true,
  workspaceId: null,
  fetchedTeamId: null,
  availableYears: [],
  availableQuarters: [],
  currentYear: null,
  currentQuarter: null,
  activeYear: null,
  activeQuarter: null,
  cycles: [],
  cycleVisions: [],
  rootTeamId: null,
  currentTeamId: null,
  setCurrentTeamId: (id) => set({ currentTeamId: id }),
  unlockedCycleKey: null,
  unlockedBaselineRockId: null,
  setUnlockedCycleKey: (key) => set({ unlockedCycleKey: key }),
  setUnlockedBaselineRockId: (rockId) => set({ unlockedBaselineRockId: rockId }),
  cycleBarOpen: false,
  goals: [],
  rocks: [],
  milestones: [],
  milestoneDependencies: [],
  rockUpdates: [],
  metrics: [],
  scorecards: [],
  scorecardMetrics: [],
  tasks: [],
  issues: [],
  people: [],
  meetings: [],
  meetingSchedules: [],
  analyzingMeetingIds: [],
  generatingRecapIds: [],
  planningChecklists: [],
  visions: [],
  ideas: [],
  planningSessions: [],
  rockDecisions: [],
  myRocks: [],
  myTasks: [],
  myIssues: [],
  myMetrics: [],
  myMilestones: [],
  myRockUpdates: [],
  crossTeamRocks: [],
  crossTeamTasks: [],
  crossTeamIssues: [],
  crossTeamMetrics: [],
  crossTeamScorecardMetricIds: [],
  crossTeamReady: false,

  /**
   * Clear domain rows after a team or account change. Zustand survives both,
   * so retaining rows can mislabel prior-team data or expose prior-user data.
   * Route/workspace/cycle pointers survive because the refetch needs them.
   */
  resetSessionView: () => set({
    currentYear: null, currentQuarter: null, cycleBarOpen: false,
    unlockedCycleKey: null, unlockedBaselineRockId: null,
  }),

  resetDomain: () => {
    _operatingDataRequestGeneration += 1;
    set({
      goals: [], rocks: [], milestones: [], milestoneDependencies: [], metrics: [], scorecards: [], scorecardMetrics: [],
      tasks: [], issues: [], people: [], meetings: [], meetingSchedules: [], planningChecklists: [], visions: [],
      ideas: [], planningSessions: [], rockDecisions: [], rockUpdates: [],
      analyzingMeetingIds: [], generatingRecapIds: [],
      myRocks: [], myTasks: [], myIssues: [], myMetrics: [], myMilestones: [], myRockUpdates: [],
      crossTeamRocks: [], crossTeamTasks: [], crossTeamIssues: [], crossTeamMetrics: [],
      crossTeamScorecardMetricIds: [], crossTeamReady: false,
    });
  },

  /**
   * Fetch Home's cross-team index, then hydrate its bounded ids. `my_items()`
   * must remain `SECURITY INVOKER`; caller RLS supplies the privacy boundary.
   */
  fetchMyItems: async (workspaceId: string) => {
    const { data: index, error } = await supabase.rpc('my_items', { _workspace_id: workspaceId });
    if (error) {
      console.error('[fetchMyItems] my_items failed', error);
      return;
    }
    const idsOf = (kind: string) =>
      (index ?? []).filter((r: any) => r.entity_type === kind).map((r: any) => r.id as string);

    const rockIds = idsOf('rock');
    const taskIds = idsOf('task');
    const issueIds = idsOf('issue');
    const metricIds = idsOf('metric');

    // `.in('id', [])` is a valid empty set in PostgREST — unlike `id.in.()` in a
    // raw `or` filter — so no guard is needed here. Skipping the round trip when
    // there is nothing to ask for is worth it anyway.
    const rows = async (table: string, ids: string[]) =>
      ids.length ? (await supabase.from(table as any).select('*').in('id', ids)).data ?? [] : [];

    const [rRows, tRows, iRows, mRows, msRows, ruRows] = await Promise.all([
      rows('rocks', rockIds),
      rows('tasks', taskIds),
      rows('issues', issueIds),
      rows('metrics', metricIds),
      rockIds.length
        ? supabase.from('milestones').select('*').in('rock_id', rockIds)
            .order('position', { ascending: true }).then((r) => r.data ?? [])
        : Promise.resolve([] as any[]),
      // Home's celebration moment reads updates to decide what just happened
      // (lib/home-moments). Scoped to my rocks for the same reason the
      // milestones are: it is the only set the page asks about.
      rockIds.length
        ? supabase.from('rock_updates').select('*').in('rock_id', rockIds)
            .order('created_at', { ascending: false }).then((r) => r.data ?? [])
        : Promise.resolve([] as any[]),
    ]);

    set({
      myRocks: (rRows as any[]).map(rockFromRow),
      myTasks: (tRows as any[]).map(taskFromRow),
      myIssues: (iRows as any[]).map(issueFromRow),
      myMetrics: (mRows as any[]).map(metricFromRow),
      myMilestones: (msRows as any[]).map(milestoneFromRow),
      myRockUpdates: (ruRows as any[]).map(rockUpdateFromRow),
    });
  },

  fetchCrossTeamTotals: async (workspaceId: string) => {
    const [rRes, tRes, iRes, kRes, qskRes] = await Promise.all([
      supabase.from('rocks').select('*').eq('workspace_id', workspaceId),
      supabase.from('tasks').select('*').eq('workspace_id', workspaceId),
      supabase.from('issues').select('*').eq('workspace_id', workspaceId),
      supabase.from('metrics').select(METRIC_SELECT).eq('workspace_id', workspaceId),
      // Only ACTIVE boards. "Is this metric still expected to be filled?" is
      // answered by whether a live board still holds it (#263), so a metric
      // that fell off every board when they were archived stops being counted
      // as missing rather than nagging forever.
      supabase.from('scorecard_metrics').select('metric_id, scorecards!inner(status)')
        .eq('workspace_id', workspaceId).eq('scorecards.status', 'active'),
    ]);
    const failed = [rRes, tRes, iRes, kRes, qskRes].find((r) => r.error);
    if (failed?.error) {
      console.error('[fetchCrossTeamTotals] failed', failed.error);
      return;
    }

    set({
      crossTeamRocks: (rRes.data ?? []).map(rockFromRow),
      crossTeamTasks: (tRes.data ?? []).map(taskFromRow),
      crossTeamIssues: (iRes.data ?? []).map(issueFromRow),
      crossTeamMetrics: (kRes.data ?? []).map(metricFromRow),
      crossTeamScorecardMetricIds: [
        ...new Set((qskRes.data ?? []).map((r: any) => r.metric_id as string).filter(Boolean)),
      ],
      crossTeamReady: true,
    });
  },

  fetchAll: async (workspaceId: string, teamId?: string | null) => {
    const requestGeneration = ++_operatingDataRequestGeneration;
    const requestedTeamId = teamId ?? null;
    const previousWorkspaceId = get().workspaceId;
    const teamChanged = get().fetchedTeamId !== requestedTeamId;
    set({ loading: true, workspaceId, fetchedTeamId: requestedTeamId });

    // ── Wave A: what "this team" means ──────────────────────────────────────
    //
    // The root has to be resolved BEFORE the scoped queries rather than beside
    // them, because a null `teamId` means "the root" and the store cannot know
    // which team that is until it asks. One extra round trip, once per fetch.
    //
    // The shares are the other half of the predicate. § 4.1's model is that a
    // team's work is `owned ∪ shared`, so a fetch that filtered on `team_id`
    // alone would drop exactly the rows the E series exists to surface — and
    // drop them silently, since RLS is perfectly happy to return them.
    const [{ data: rootTeamRow }, { data: shareRows }] = await Promise.all([
      supabase.from('teams').select('id').eq('workspace_id', workspaceId).eq('is_root', true).maybeSingle(),
      teamId
        ? supabase.from('entity_shares').select('entity_type, entity_id').eq('team_id', teamId)
        : Promise.resolve({ data: [] as { entity_type: string; entity_id: string }[] }),
    ]);
    const rootId: string | null = (rootTeamRow as any)?.id ?? null;
    const here = teamId ?? rootId;

    // Shares for the root were not fetched above (nothing points AT the root
    // until someone shares upward), so this is re-asked once `here` is known.
    const sharedIds = (kind: string): string[] =>
      (shareRows ?? []).filter((s: any) => s.entity_type === kind).map((s: any) => s.entity_id);

    /**
     * `team_id = here OR id ∈ shared-to-here`, as one PostgREST filter.
     *
     * The empty-list guard is not defensive tidiness: `id.in.()` is a syntax
     * error, not an empty set, so a team with no inbound shares would fail the
     * whole query rather than return its own rows.
     */
    const scoped = <T>(q: T, kind: string): T => {
      if (!here) return q;
      const ids = sharedIds(kind);
      const anyQ = q as any;
      return ids.length
        ? anyQ.or(`team_id.eq.${here},id.in.(${ids.join(',')})`)
        : anyQ.eq('team_id', here);
    };

    // ── Wave B ───────────────────────────────────────────────────────────────
    //
    // NOT scoped, each for its own reason, and each deliberate:
    //
    //   · `visions` / `cycle_visions` — vision RESOLVES UP the team chain
    //     (#150). A team with no vision of its own inherits its ancestor's, so
    //     filtering to `team_id = here` would empty the bulletin for every
    //     team that has not forked one. Scoping these is #150's job and needs
    //     the ancestor walk; doing it here would be a visible change, which is
    //     the one thing this issue must not make.
    //   · `goals` — no surface scopes goals by team today, so narrowing the
    //     fetch would change what RocksSection shows. Out of remit.
    //   · `cycles`, `people`, `planning_checklists` — no
    //     `team_id` at all, or small enough that a second query costs more than
    //     the rows.
    //
    // `metrics` is missing from this wave on purpose — see wave C.
    const [
      { data: wsRow },
      { data: tmData }, { data: gData }, { data: rData }, { data: msData }, { data: mdData },
      { data: scData }, { data: qskData },
      { data: aData }, { data: iData },
      { data: wmData },
      { data: pcData },
      { data: visionRows },
      { data: ideaData }, { data: psData }, { data: psdData },
      { data: ruData },
      { data: cycData },
      { data: cvData },
      { data: schedData },
    ] = await Promise.all([
      supabase.from('workspaces').select('active_year, active_quarter').eq('id', workspaceId).maybeSingle(),
      supabase.from('people').select('*').eq('workspace_id', workspaceId),
      supabase.from('goals').select('*').eq('workspace_id', workspaceId),
      scoped(supabase.from('rocks').select('*').eq('workspace_id', workspaceId), 'rock'),
      // Milestones and rock updates carry no `team_id` — they belong to a rock,
      // and follow it through an inner join rather than a column of their own.
      // `referencedTable` puts the filter on the embedded rock, which is the
      // only way to say "the rocks this fetch is keeping" in one query.
      scopedVia(supabase.from('milestones').select('*, rocks!inner(id)').eq('workspace_id', workspaceId)
        .order('position', { ascending: true }), 'rocks', here, sharedIds('rock')),
      // Not team-scoped in the query: an edge has no team of its own and its
      // read policy already resolves one through the successor's rock. Edges
      // whose milestones fall outside this fetch are simply ignored by the
      // schedule, which only walks endpoints it holds.
      supabase.from('milestone_dependencies').select('*').eq('workspace_id', workspaceId),
      scoped(supabase.from('scorecards').select('*').eq('workspace_id', workspaceId), 'scorecard'),
      scopedVia(supabase.from('scorecard_metrics').select('*, scorecards!inner(id)').eq('workspace_id', workspaceId),
        'scorecards', here, sharedIds('scorecard')),
      scoped(supabase.from('tasks').select('*').eq('workspace_id', workspaceId), 'task'),
      scoped(supabase.from('issues').select('*').eq('workspace_id', workspaceId), 'issue'),
      // Meetings carry `team_id` and are not shareable — a meeting belongs to
      // the team that holds it, full stop.
      here ? supabase.from('meetings').select('*').eq('workspace_id', workspaceId).eq('team_id', here)
           : supabase.from('meetings').select('*').eq('workspace_id', workspaceId),
      here ? supabase.from('planning_checklists').select('*').eq('workspace_id', workspaceId).eq('team_id', here)
           : supabase.from('planning_checklists').select('*').eq('workspace_id', workspaceId),
      supabase.from('visions').select('*').eq('workspace_id', workspaceId),
      scoped(supabase.from('ideas').select('*').eq('workspace_id', workspaceId), 'idea'),
      here ? supabase.from('planning_sessions').select('*').eq('workspace_id', workspaceId).eq('team_id', here)
           : supabase.from('planning_sessions').select('*').eq('workspace_id', workspaceId),
      supabase.from('rock_decisions').select('*').eq('workspace_id', workspaceId),
      scopedVia(supabase.from('rock_updates').select('*, rocks!inner(id)').eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false }), 'rocks', here, sharedIds('rock')),
      supabase.from('cycles').select('*').eq('workspace_id', workspaceId),
      // cycle_visions carries no workspace_id — it reaches the tenant through
      // its cycle, so the filter runs as an inner join rather than a column eq.
      supabase.from('cycle_visions').select('id, cycle_id, team_id, vision_id, cycles!inner(workspace_id)')
        .eq('cycles.workspace_id', workspaceId),
      // Team-scoped like the meetings they produce, and for the same reason: a
      // series belongs to the team that holds it.
      here ? supabase.from('meeting_schedules').select('*').eq('workspace_id', workspaceId).eq('team_id', here)
           : supabase.from('meeting_schedules').select('*').eq('workspace_id', workspaceId),
    ]);

    // ── Wave C: metrics, which are NOT simply "this team's metrics" ─────────
    //
    // THE TRAP THIS WAVE EXISTS FOR. `metric_eligible_for_scorecard` has four
    // clauses (§ 4.3.1): same team, open home, metric shared to here, or metric
    // on a board shared to here. So a board legitimately carries metrics homed
    // on other teams — company numbers on a team's scorecard is the ordinary
    // case, not the edge one — and scoping metrics by `team_id ∪ shared` would
    // have emptied rows out of scorecards with nothing raised anywhere.
    //
    // Hence the third arm: whatever the boards we just loaded actually
    // reference. It has to come after wave B because it is not knowable until
    // `scorecard_metrics` has resolved.
    const referencedMetricIds = [...new Set((qskData ?? []).map((r: any) => r.metric_id).filter(Boolean))];
    const metricIds = [...new Set([...sharedIds('metric'), ...referencedMetricIds])] as string[];
    const { data: kData } = await (
      !here
        ? supabase.from('metrics').select(METRIC_SELECT).eq('workspace_id', workspaceId)
        : metricIds.length
          ? supabase.from('metrics').select(METRIC_SELECT).eq('workspace_id', workspaceId)
              .or(`team_id.eq.${here},id.in.(${metricIds.join(',')})`)
          : supabase.from('metrics').select(METRIC_SELECT).eq('workspace_id', workspaceId).eq('team_id', here)
    );

    if (
      requestGeneration !== _operatingDataRequestGeneration
      || get().workspaceId !== workspaceId
      || get().fetchedTeamId !== requestedTeamId
    ) return;

    const activeYear: number | null = (wsRow as any)?.active_year ?? null;
    const activeQuarter: number | null = (wsRow as any)?.active_quarter ?? null;


    const people: Person[] = (tmData ?? []).map((r: any) => ({
      id: r.id, name: r.name, initials: r.initials, role: r.role ?? undefined, archived: r.archived ?? false, featured: r.featured ?? false,
      badgeColor: isBadgeColor(r.badge_color) ? r.badge_color : undefined,
    }));
    // Lazy-assign badge colors to members who don't have one yet.
    //
    // Best-effort, and deliberately NOT routed through applyWrite. Updating
    // people needs owner/admin/manager, so for an ordinary member this
    // write is refused every time — and applyWrite would answer that with a
    // toast plus a full domain refetch, which re-enters this loop and refuses
    // again. The color is already in local state above, so a viewer who cannot
    // persist it still sees the right avatar; the next manager to load the
    // workspace writes it.
    for (const member of people) {
      if (!member.badgeColor) {
        const color = pickBadgeColor(member.initials, people.filter((m) => m.id !== member.id));
        member.badgeColor = color;
        // No .select(): a refusal here arrives as zero rows and no error, and
        // asking for the rows back only exists to tell those apart. Nothing
        // downstream needs to. A real error — network, constraint — still logs.
        void coreApi(`/records/people/${member.id}`, { badge_color: color, quiet: true }, 'PATCH')
          .then(({ error }) => {
            if (error && error.code !== '42501') console.warn('[fetchAll:badgeColor] not persisted', error.message);
          });
      }
    }
    people.sort((a, b) => a.name.localeCompare(b.name));
    const goals: AnnualGoal[] = (gData ?? []).map((r: any) => ({
      id: r.id, title: r.title, description: r.description ?? undefined,
      year: r.year, teamId: r.team_id ?? undefined,
    }));
    const rocks: Rock[] = (rData ?? []).map(rockFromRow);
    const milestones: Milestone[] = (msData ?? []).map(milestoneFromRow);
    const milestoneDependencies: MilestoneDependency[] = (mdData ?? []).map(milestoneDependencyFromRow);
    const metrics: Metric[] = (kData ?? []).map(metricFromRow);
    const scorecards: Scorecard[] = (scData ?? []).map(scorecardFromRow);
    const scorecardMetrics: ScorecardMetric[] = (qskData ?? []).map((r: any) => ({
      id: r.id,
      scorecardId: r.scorecard_id,
      metricId: r.metric_id,
    }));
    const tasks: Task[] = (aData ?? []).map(taskFromRow);
    const issues: Issue[] = (iData ?? []).map(issueFromRow);

    const meetingSchedules: MeetingSchedule[] = (schedData ?? []).map(meetingScheduleFromRow);
    const meetings: Meeting[] = (wmData ?? []).map(meetingFromRow);

    const planningChecklists: PlanningChecklist[] = (pcData ?? []).map(planningChecklistFromRow);

    const visions: Vision[] = (visionRows ?? []).map(visionFromRow);

    const ideas: Idea[] = (ideaData ?? []).map((r: any) => ({
      id: r.id, workspaceId: r.workspace_id,
      title: r.title, description: r.description ?? undefined,
      status: r.status,
      targetQuarter: r.target_quarter ?? undefined,
      ownerId: r.owner_id ?? undefined,
      goalId: r.goal_id ?? undefined,
      milestones: Array.isArray(r.milestones) ? r.milestones : [],
      linkedMetricIds: Array.isArray(r.linked_metric_ids) ? r.linked_metric_ids : [],
      promotedRockId: r.promoted_rock_id ?? undefined,
      planningSessionId: r.planning_session_id ?? undefined,
      sourceRockId: r.source_rock_id ?? undefined,
      sourceGoalId: r.source_goal_id ?? undefined,
      sourceOwnerId: r.source_owner_id ?? undefined,
      sourceMilestones: Array.isArray(r.source_milestones) ? r.source_milestones : [],
      combinedIntoId: r.combined_into_id ?? undefined,
      sourceIssueId: r.source_issue_id ?? undefined,
      generatedByPrompt: r.generated_by_prompt ?? undefined,
      createdBy: r.created_by ?? undefined,
      teamId: r.team_id ?? undefined,
      createdAt: r.created_at,
    }));
    const planningSessions: PlanningSession[] = (psData ?? []).map(planningSessionFromRow);
    const rockDecisions: RockDecision[] = (psdData ?? []).map((r: any) => ({
      id: r.id, workspaceId: r.workspace_id,
      sessionId: r.session_id,
      step: r.step,
      ideaKind: r.idea_kind ?? undefined,
      ideaId: r.idea_id ?? undefined,
      rockId: r.rock_id ?? undefined,
      decision: r.decision,
      combinedIntoId: r.combined_into_id ?? undefined,
      promotedTargetId: r.promoted_target_id ?? undefined,
      rationale: r.rationale ?? undefined,
      decidedAt: r.decided_at,
      decidedBy: r.decided_by ?? undefined,
    }));

    // Derive available years and quarters from the union across all entity data + active cycle.
    const yearSet = new Set<number>();
    const quarterSet = new Set<string>(); // "year-quarter"
    const collect = (year: any, quarter?: any) => {
      if (typeof year === 'number') {
        yearSet.add(year);
        if (typeof quarter === 'number' && quarter >= 1 && quarter <= 4) {
          quarterSet.add(`${year}-${quarter}`);
        }
      }
    };
    for (const g of gData ?? []) collect((g as any).year);
    for (const r of rData ?? []) collect((r as any).year, (r as any).quarter);
    for (const r of qskData ?? []) collect((r as any).year, (r as any).quarter);
    for (const r of aData ?? []) collect((r as any).year, (r as any).quarter);
    for (const r of iData ?? []) collect((r as any).year, (r as any).quarter);
    for (const r of wmData ?? []) collect((r as any).year, (r as any).quarter);
    for (const r of pcData ?? []) collect((r as any).year, (r as any).quarter);
    for (const r of psData ?? []) {
      collect((r as any).year, (r as any).quarter);
      collect((r as any).prev_year, (r as any).prev_quarter);
    }
    if (activeYear != null) {
      yearSet.add(activeYear);
      if (activeQuarter != null) quarterSet.add(`${activeYear}-${activeQuarter}`);
    }

    const cycles: Cycle[] = (cycData ?? []).map((r: any) => ({
      id: r.id,
      workspaceId: r.workspace_id,
      year: r.year,
      quarter: r.quarter,
      state: r.state,
      activatedAt: r.activated_at ?? undefined,
    }));
    for (const c of cycles) yearSet.add(c.year);

    const cycleVisions: CycleVision[] = (cvData ?? []).map((r: any) => ({
      id: r.id,
      cycleId: r.cycle_id,
      teamId: r.team_id,
      visionId: r.vision_id ?? null,
    }));
    const rootTeamId: string | null = rootId;

    const availableYears: number[] = [...yearSet].sort((a, b) => a - b);
    const derivedQuarters: { year: number; quarter: number }[] = [...quarterSet]
      .map((s) => { const [y, q] = s.split('-').map(Number); return { year: y, quarter: q }; });
    // Union with the cycles table so a quarter that's been planned but has no
    // data in it yet still shows up in the switcher.
    const availableQuarters = mergeAvailableQuarters(derivedQuarters, cycles);

    const rockUpdates: RockUpdate[] = (ruData ?? []).map(rockUpdateFromRow);

    const view = resolveDisplayedCycle({
      workspaceId,
      previousWorkspaceId,
      teamChanged,
      displayed: { year: get().currentYear, quarter: get().currentQuarter },
      active: { year: activeYear, quarter: activeQuarter },
      availableQuarters,
    });

    set({
      loading: false,
      availableYears,
      availableQuarters,
      currentYear: view.year,
      currentQuarter: view.quarter,
      activeYear,
      activeQuarter,
      cycles,
      cycleVisions,
      rootTeamId,
      unlockedCycleKey: view.carried ? get().unlockedCycleKey : null,
      unlockedBaselineRockId: view.carried ? get().unlockedBaselineRockId : null,
      // `cycleBarOpen` is deliberately NOT reset here. A deep link carrying
      // ?year=&quarter= raises the bar at mount, and fetchAll resolves after
      // that — clearing it here silently closed the bar the link had opened.
      people, goals, rocks, milestones, milestoneDependencies, rockUpdates, metrics, scorecards, scorecardMetrics,
      tasks, issues, meetings, meetingSchedules, planningChecklists, visions,
      ideas, planningSessions, rockDecisions,
    });

  },

  // Displayed year/quarter (UI only — does not persist)
  setCurrentYear: (year) => {
    const { availableQuarters, activeYear, activeQuarter } = get();
    const qsForYear = availableQuarters
      .filter((q) => q.year === year)
      .sort((a, b) => b.quarter - a.quarter);
    const keepQ = activeYear === year ? qsForYear.find((q) => q.quarter === activeQuarter) : null;
    const pickedQ = keepQ ?? qsForYear[0] ?? null;
    set({ currentYear: year, currentQuarter: pickedQ?.quarter ?? null });
  },

  setCurrentQuarter: (year, quarter) => {
    set((s) => ({
      currentYear: year,
      currentQuarter: quarter,
      // An unlock belongs to the cycle you unlocked and to the moment you were
      // looking at it. Carrying it across a navigation is how someone ends up
      // editing history they thought was locked, two quarters away from where
      // they lifted the guard.
      unlockedCycleKey: s.unlockedCycleKey === cycleKey(year, quarter) ? s.unlockedCycleKey : null,
    }));
  },

  setCycleBarOpen: (open) => set({ cycleBarOpen: open }),

  // Active year/quarter — persisted on workspaces.active_year / active_quarter.
  setActiveYear: async (year) => {
    const { workspaceId, activeYear, activeQuarter } = get();
    if (!workspaceId) return;
    const keepQuarter = activeYear === year ? activeQuarter : null;
    await coreApi(`/workspaces/${workspaceId}/active-cycle`, { year, quarter: keepQuarter });
    set({ activeYear: year, activeQuarter: keepQuarter, currentYear: year, currentQuarter: keepQuarter });
  },

  // ── Cycle state ─────────────────────────────────────────────────────────
  // Two verbs that differ only in whether they move the primary pointer.
  // Deriving "primary" from the most recently activated cycle would break the
  // boundary case: with Q4 primary, reopening Q3 to finish wrapping up would
  // yank the pointer backwards.

  activateCycle: async (year, quarter, opts) => {
    const { workspaceId, activeYear, activeQuarter, cycles } = get();
    if (!workspaceId) return;
    const newKey = cycleKey(year, quarter);
    // Running several quarters at once is a legitimate exception, not the norm —
    // so activation offers to close the rest in one go rather than silently
    // leaving them open or silently closing them. The caller decides; the CTA
    // points at normal usage.
    const toClose = opts?.closeOthers
      ? cycles.filter((c) => c.state === 'open' && cycleKey(c.year, c.quarter) !== newKey)
      : [];
    const { data: sent, error: activateError } = await coreApi(`/workspaces/${workspaceId}/cycles/activate`, {
      year, quarter, close_others: !!opts?.closeOthers,
    });
    const activated = sent?.[0] as { cycle?: { id?: string }; stamped?: number } | undefined;
    const activatedId = activated?.cycle?.id ?? null;
    const stamped = activated?.stamped ?? 0;
    if (activateError) {
      console.error('[activateCycle] failed', activateError);
      storeNotice.error("Couldn't activate that cycle.");
      return;
    }
    if (stamped > 0) {
      set((st) => ({
        milestones: st.milestones.map((m) => {
          const parent = st.rocks.find((r) => r.id === m.rockId);
          if (!parent || parent.year !== year || parent.quarter !== quarter) return m;
          if (!m.endDate || m.baselineEnd) return m;
          return { ...m, baselineStart: m.startDate, baselineEnd: m.endDate };
        }),
      }));
    }

    set((s) => {
      let next = withCycleState(s.cycles, workspaceId, year, quarter, 'open', true, activatedId);
      for (const c of toClose) next = withCycleState(next, workspaceId, c.year, c.quarter, 'closed', false);
      const hasYear = s.availableYears.includes(year);
      const hasQuarter = quarter == null
        || s.availableQuarters.some((q) => q.year === year && q.quarter === quarter);
      return {
        availableYears: hasYear ? s.availableYears : [...s.availableYears, year].sort((a, b) => a - b),
        availableQuarters: hasQuarter ? s.availableQuarters
          : [...s.availableQuarters, { year, quarter }].sort((a, b) => a.year - b.year || a.quarter - b.quarter),
        activeYear: year, activeQuarter: quarter,
        currentYear: year, currentQuarter: quarter,
        cycles: next,
        // Any unlock belonged to a cycle we may have just closed or moved past.
        unlockedCycleKey: null,
        // Activation re-agrees the plan, which is the opposite of holding a
        // baseline open for editing.
        unlockedBaselineRockId: null,
      };
    });
    // Activation is tenant-wide but a run is per team, so every team's run for
    // this cycle closes with it — every team the tenant calendar reaches. An
    // admin has no write onto a secret team and none into an archived one, so
    // their runs are theirs to close, the same two exclusions the checklist
    // fan-out applies.
    const teams = useTeamsStore.getState().teams;
    const teamById = new Map(teams.map((s) => [s.id, s]));
    const calendarReaches = (teamId: string | null | undefined): boolean => {
      const team = teamId ? teamById.get(teamId) : undefined;
      if (!team) return true;
      if (team.privacy === 'secret') return false;
      return !ancestorChain(teams, team.id).some((id) => teamById.get(id)?.archived);
    };
    const runsToClose = get().planningSessions.filter(
      (p) => p.kind === 'quarterly' && (p.year ?? null) === year
        && (p.quarter ?? null) === quarter && !p.completedAt
        && calendarReaches(p.teamId),
    );
    for (const run of runsToClose) await get().completePlanningSession(run.id);
  },

  reopenCycle: async (year, quarter) => {
    const { workspaceId } = get();
    if (!workspaceId) return;
    const reopenedId = await upsertCycleRow(workspaceId, year, quarter, 'open');
    set((s) => ({ cycles: withCycleState(s.cycles, workspaceId, year, quarter, 'open', false, reopenedId) }));
  },

  closeCycle: async (year, quarter) => {
    const { workspaceId, activeYear, activeQuarter } = get();
    if (!workspaceId) return;
    // The primary must always be open — activate a different cycle first,
    // rather than leaving the workspace pointing at somewhere unwritable.
    if (year === activeYear && (quarter ?? null) === (activeQuarter ?? null)) return;
    const closedId = await upsertCycleRow(workspaceId, year, quarter, 'closed');
    set((s) => ({
      cycles: withCycleState(s.cycles, workspaceId, year, quarter, 'closed', false, closedId),
      // Closing a cycle drops any unlock on it.
      unlockedCycleKey: s.unlockedCycleKey === cycleKey(year, quarter) ? null : s.unlockedCycleKey,
    }));
  },

  setUnlockedCycle: (key) => set({ unlockedCycleKey: key }),

  ensureYear: async (year) => {
    const { availableYears } = get();
    if (availableYears.includes(year)) return;
    set((s) => ({ availableYears: [...s.availableYears, year].sort((a, b) => a - b) }));
  },

  ensureQuarter: async (year, quarter) => {
    const alreadyExists = get().availableQuarters.some((q) => q.year === year && q.quarter === quarter);
    set((s) => {
      if (s.availableQuarters.some((q) => q.year === year && q.quarter === quarter)) return s;
      return {
        availableQuarters: [...s.availableQuarters, { year, quarter }]
          .sort((a, b) => a.year - b.year || a.quarter - b.quarter),
      };
    });
    if (!alreadyExists) {
      // Every path into this function is "create a quarter in order to plan it",
      // so the cycle row is born in `planning`. Without it the new quarter has no
      // row at all — writable by the missing-row escape hatch, but invisible to
      // anything that asks what state it's in, including the switcher's dashed
      // cell and Planning's own "what can I activate" ladder.
      const { workspaceId, cycles } = get();
      if (workspaceId) {
        // No cycle at all yet, anywhere: this is the workspace's very first
        // one, so its existing (bootstrap-seeded) vision becomes the origin
        // revision point — nothing to branch from yet.
        const isFirstCycleEver = cycles.length === 0;
        // A cycle exists, but none in this year yet: a new year has started,
        // which is the one automatic vision write (see branchVisionForYear).
        const isFirstCycleOfYear = !isFirstCycleEver && !cycles.some((c) => c.year === year);
        const plannedId = await upsertCycleRow(workspaceId, year, quarter, 'planning');
        set((s) => ({ cycles: withCycleState(s.cycles, workspaceId, year, quarter, 'planning', false, plannedId) }));
        if (isFirstCycleEver) {
          const only = get().visions[0];
          if (only) await get().setVisionDecision(year, quarter, only.id);
        } else if (isFirstCycleOfYear) {
          await get().branchVisionForYear(year, quarter);
        }
      }
    }
  },

  // Goals
  addGoal: (goal) => {
    const { workspaceId } = get();
    // Goals are year-scoped, so this checks the *annual* cycle (quarter null).
    // A no-op until annual planning exists and starts creating those rows —
    // wired now so goals aren't the one cycle-scoped entity with no gate.
    if (blockedByCycle(get(), goal.year, null)) return;
    set((s) => ({ goals: [...s.goals, { ...goal, teamId: goal.teamId ?? newEntityTeamId(get()) }] }));
    const teamId = newEntityTeamId(get());
    applyWrite(coreApi('/records/goals', {
      ...(teamId ? { team_id: teamId } : {}),
      id: goal.id, title: goal.title, description: goal.description ?? null,
      year: goal.year,
      workspace_id: workspaceId,
      unlocked_cycle: get().unlockedCycleKey,
    }), {
      noun: 'this goal',
      context: 'addGoal',
      rollback: () => set((st) => ({ goals: st.goals.filter((g) => g.id !== goal.id) })),
    });
  },
  updateGoal: (id, updates) => {
    const before = get().goals.find((g) => g.id === id);
    if (before && blockedByCycle(get(), before.year, null)) return;
    set((s) => ({ goals: s.goals.map((g) => g.id === id ? { ...g, ...updates } : g) }));
    const dbUpdates: any = {};
    if (updates.title !== undefined) dbUpdates.title = updates.title;
    if (updates.description !== undefined) dbUpdates.description = updates.description;
    if (updates.year !== undefined) dbUpdates.year = updates.year;
    if (Object.keys(dbUpdates).length) {
      applyWrite(coreApi(`/records/goals/${id}`, { ...dbUpdates, unlocked_cycle: get().unlockedCycleKey }, 'PATCH'), {
        noun: 'this goal',
        context: 'updateGoal',
        rollback: () => {
          if (before) set((st) => ({ goals: st.goals.map((x) => (x.id === id ? before : x)) }));
        },
      });
    }
  },
  deleteGoal: (id) => {
    const before = get().goals.find((g) => g.id === id);
    if (before && blockedByCycle(get(), before.year, null)) return;
    // Rocks reference goals via a FK with no ON DELETE clause — unlink them
    // first (they're kept, per the delete-confirmation copy) or the goal
    // delete fails with a 409 (foreign_key_violation) and never persists.
    set((s) => ({
      goals: s.goals.filter((g) => g.id !== id),
      rocks: s.rocks.map((r) => r.goalId === id ? { ...r, goalId: undefined } : r),
    }));
    applyWrite(coreApi(`/records/goals/${id}`, { unlocked_cycle: get().unlockedCycleKey }, 'DELETE'), {
      noun: 'this goal',
      context: 'deleteGoal',
      operation: 'delete',
      refetch: refetchDomain(get),
    });
  },

  // Rocks
  addRock: (rock) => rockMutations.addRock(activeContext(get), rock, {
    apply: (final) => set((s) => ({ rocks: [...s.rocks, final] })),
    rollback: () => set((st) => ({ rocks: st.rocks.filter((r) => r.id !== rock.id) })),
  }),
  updateRock: (id, updates, opts) => {
    // `myRocks` is fetched separately and is not a subset of `rocks`: a rock of
    // mine on another team is in one and not the other. Both slices are patched
    // together, or Home's card writes and never redraws.
    const before = get().rocks.find((r) => r.id === id) ?? get().myRocks.find((r) => r.id === id);
    return rockMutations.updateRock(activeContext(get), id, updates, before, {
      apply: (patch) => set((s) => ({
        rocks: s.rocks.map((r) => r.id === id ? { ...r, ...patch } : r),
        myRocks: s.myRocks.map((r) => r.id === id ? { ...r, ...patch } : r),
      })),
      rollback: () => {
        if (before) set((st) => ({
          rocks: st.rocks.map((x) => (x.id === id ? before : x)),
          myRocks: st.myRocks.map((x) => (x.id === id ? before : x)),
        }));
      },
      recordStatusChange: opts?.skipStatusRecord ? undefined : (status, updateId) => {
        void resolveActor().then((actor) => {
          const workspaceId = get().workspaceId;
          if (!actor || !workspaceId) return;
          set((s) => ({
            rockUpdates: [{
              id: updateId, workspaceId, rockId: id,
              authorId: actor.userId, authorMemberId: actor.memberId ?? undefined,
              statusAtUpdate: status, isSystemGenerated: true, createdAt: new Date().toISOString(),
            }, ...s.rockUpdates],
          }));
        }).catch(() => {});
      },
    });
  },
  deleteRock: (id) => {
    const before = get().rocks.find((r) => r.id === id) ?? get().myRocks.find((r) => r.id === id);
    return rockMutations.deleteRock(activeContext(get), id, before, {
      apply: () => set((s) => ({
        rocks: s.rocks.filter((r) => r.id !== id),
        myRocks: s.myRocks.filter((r) => r.id !== id),
      })),
    });
  },

  // Milestones — no cycle of their own; they follow their parent rock's.
  addMilestone: async (milestone) => {
    const { workspaceId } = get();
    if (!workspaceId) return;
    const parent = get().rocks.find((r) => r.id === milestone.rockId);
    if (parent && blockedByCycle(get(), parent.year, parent.quarter)) return;
    const selfBaselines = !!milestone.endDate
      && !cycleIsDraft(get().cycles, parent?.year, parent?.quarter);
    set((s) => ({
      milestones: [...s.milestones, selfBaselines
        ? { ...milestone, baselineStart: milestone.startDate, baselineEnd: milestone.endDate }
        : milestone],
    }));
    await coreApi('/records/milestones', {
      id: milestone.id,
      workspace_id: workspaceId,
      rock_id: milestone.rockId,
      title: milestone.title,
      completed: milestone.completed,
      owner_id: milestone.ownerId ?? null,
      start_date: milestone.startDate ?? null,
      end_date: milestone.endDate ?? null,
      weight: milestone.weight ?? null,
      position: milestone.position,
      source_milestone_id: milestone.sourceMilestoneId ?? null,
      unlocked_cycle: get().unlockedCycleKey,
    });
  },
  updateMilestone: async (id, updates) => {
    const before = get().milestones.find((m) => m.id === id);
    if (before && milestoneCycleBlocked(get(), before.rockId)) return;
    // OwnerBadge's "Unassigned" row reports '' rather than null, and '' is not
    // nullish — so `milestone.ownerId ?? rock.ownerId` stopped falling back and
    // the milestone read as genuinely unowned, on its row and on the chart.
    // Normalised at the boundary, as `updateRock` already does.
    const patch = 'ownerId' in updates
      ? { ...updates, ownerId: updates.ownerId || undefined }
      : updates;
    set((s) => ({ milestones: s.milestones.map((m) => m.id === id ? { ...m, ...patch } : m) }));
    const dbUpdates: any = {};
    if (updates.title !== undefined) dbUpdates.title = updates.title;
    if (updates.completed !== undefined) dbUpdates.completed = updates.completed;
    if (updates.startDate !== undefined) dbUpdates.start_date = updates.startDate ?? null;
    if (updates.endDate !== undefined) dbUpdates.end_date = updates.endDate ?? null;
    if (updates.ownerId !== undefined) dbUpdates.owner_id = updates.ownerId || null;
    if (updates.weight !== undefined) dbUpdates.weight = updates.weight ?? null;
    if (updates.position !== undefined) dbUpdates.position = updates.position;
    // While the baseline is unlocked, a date edit re-agrees the plan rather
    // than slipping from it, so the baseline travels with the date. Only the
    // dates being written move; an unlock is not a licence to rewrite the rest.
    const rebaselined = !!before
      && get().unlockedBaselineRockId === before.rockId
      && (updates.startDate !== undefined || updates.endDate !== undefined);
    if (rebaselined) {
      set((s) => ({
        milestones: s.milestones.map((m) => m.id === id ? {
          ...m,
          baselineStart: updates.startDate !== undefined ? updates.startDate : m.baselineStart,
          baselineEnd: updates.endDate !== undefined ? updates.endDate : m.baselineEnd,
        } : m),
      }));
    }
    const { error } = await coreApi(`/records/milestones/${id}`, {
      ...dbUpdates,
      ...(rebaselined ? { rebaseline: true } : {}),
      unlocked_cycle: get().unlockedCycleKey,
    }, 'PATCH');
    if (error) {
      console.error('[updateMilestone] failed', error.message);
      if (before) set((s) => ({ milestones: s.milestones.map((m) => m.id === id ? before : m) }));
      storeNotice.error('That milestone could not be saved.');
    }
  },
  deleteMilestone: async (id) => {
    const before = get().milestones.find((m) => m.id === id);
    if (before && milestoneCycleBlocked(get(), before.rockId)) return;
    set((s) => ({
      milestones: s.milestones.filter((m) => m.id !== id),
      // The database cascades these; local state has to be told.
      milestoneDependencies: s.milestoneDependencies.filter(
        (d) => d.predecessorId !== id && d.successorId !== id,
      ),
    }));
    await coreApi(`/records/milestones/${id}`, { unlocked_cycle: get().unlockedCycleKey }, 'DELETE');
  },
  rescheduleMilestone: async (id, startDate, endDate, opts) => {
    const before = get().milestones.find((m) => m.id === id);
    if (!before) return null;
    if (milestoneCycleBlocked(get(), before.rockId)) return null;

    const unlockedRockId = get().unlockedBaselineRockId;
    const { data: sent, error } = await coreApi(`/milestones/${id}/reschedule`, {
      start_date: startDate ?? null, end_date: endDate ?? null,
      cascade: opts?.cascade ?? true,
      ...(unlockedRockId ? { rebaseline_rock_id: unlockedRockId } : {}),
      unlocked_cycle: get().unlockedCycleKey,
    });
    const data = sent?.[0];

    if (error) {
      console.error('[rescheduleMilestone] cascade refused', error);
      storeNotice.error("You don't have access to change this milestone.");
      return null;
    }

    const result = (data ?? {}) as {
      delta?: number;
      moved?: {
        id: string; title: string; ownerId?: string | null;
        days?: number; from: string; to: string;
        startDate?: string; endDate?: string;
      }[];
      skipped?: { id: string; title: string; reason: string }[];
    };
    const moved = result.moved ?? [];
    const skipped = result.skipped ?? [];

    // Local state follows what the database says it did, not what was asked
    // for. Each milestone carries its own dates rather than sharing one delta:
    // slack means two milestones in the same cascade can move by different
    // amounts, or one of them not at all.
    const movedById = new Map(moved.map((x) => [x.id, x]));
    set((s) => ({
      milestones: s.milestones.map((m) => {
        if (m.id === id) return { ...m, startDate, endDate };
        const shift = movedById.get(m.id);
        if (!shift) return m;
        return { ...m, startDate: shift.startDate, endDate: shift.endDate };
      }),
    }));

    const rebaselinedIds = new Set<string>();
    if (unlockedRockId) {
      const rebaselined = [{ id, startDate, endDate }, ...moved.map((x) => ({
        id: x.id, startDate: x.startDate, endDate: x.endDate,
      }))].filter((x) => get().milestones.find((m) => m.id === x.id)?.rockId === unlockedRockId);
      for (const x of rebaselined) rebaselinedIds.add(x.id);
      set((s) => ({
        milestones: s.milestones.map((m) => rebaselinedIds.has(m.id)
          ? { ...m, baselineStart: m.startDate, baselineEnd: m.endDate }
          : m),
      }));
    }

    // Two different reasons a milestone stayed put, and they call for different
    // action: a closed quarter is nobody's to reopen, while a milestone on
    // someone else's rock needs that person to move it.
    const locked = skipped.filter((x) => x.reason === 'closed_cycle');
    const refused = skipped.filter((x) => x.reason === 'not_authorised');
    if (locked.length) {
      storeNotice.warning(
        locked.length === 1
          ? `"${locked[0].title}" is in a closed quarter and did not move.`
          : `${locked.length} items are in closed quarters and did not move.`,
      );
    }
    if (refused.length) {
      const { pl, withArticle } = storeTerms();
      storeNotice.warning(
        refused.length === 1
          ? `"${refused[0].title}" is on ${withArticle('rock').toLowerCase()} you don't own and did not move.`
          : `${refused.length} items are on ${pl('rock').toLowerCase()} you don't own and did not move.`,
      );
    }

    return {
      moved: moved.map((x) => ({ id: x.id, title: x.title, ownerId: x.ownerId ?? undefined })),
      skipped: skipped.map((x) => ({ id: x.id, title: x.title })),
    };
  },

  addMilestoneDependency: async (predecessorId, successorId, opts) => {
    const lagDays = opts?.lagDays ?? 0;
    const type = opts?.type ?? 'fs';
    const { workspaceId } = get();
    if (!workspaceId) return;
    const before = get().milestones.find((m) => m.id === successorId);
    if (before && milestoneCycleBlocked(get(), before.rockId)) return;

    const id = crypto.randomUUID();
    const edge: MilestoneDependency = { id, predecessorId, successorId, lagDays, type };
    set((s) => ({ milestoneDependencies: [...s.milestoneDependencies, edge] }));

    // `workspace_id` is sent because the column is NOT NULL, but the database
    // overwrites it from the two milestones — the same trigger that refuses an
    // edge across teams or one that would close a loop.
    applyWrite(coreApi('/records/milestone-dependencies', {
      id, workspace_id: workspaceId,
      predecessor_id: predecessorId, successor_id: successorId,
      lag_days: lagDays, dependency_type: type,
      unlocked_cycle: get().unlockedCycleKey,
    }), {
      noun: 'this dependency', context: 'addMilestoneDependency',
      rollback: () => set((st) => ({
        milestoneDependencies: st.milestoneDependencies.filter((d) => d.id !== id),
      })),
    });
  },

  updateMilestoneDependency: async (id, patch) => {
    const before = get().milestoneDependencies.find((d) => d.id === id);
    if (!before) return;
    const successor = get().milestones.find((m) => m.id === before.successorId);
    if (successor && milestoneCycleBlocked(get(), successor.rockId)) return;

    set((s) => ({
      milestoneDependencies: s.milestoneDependencies.map((d) => d.id === id ? { ...d, ...patch } : d),
    }));
    const dbPatch: Record<string, unknown> = {};
    if (patch.lagDays !== undefined) dbPatch.lag_days = patch.lagDays;
    if (patch.type !== undefined) dbPatch.dependency_type = patch.type;
    applyWrite(coreApi(`/records/milestone-dependencies/${id}`, { ...dbPatch, unlocked_cycle: get().unlockedCycleKey }, 'PATCH'), {
      noun: 'this dependency', context: 'updateMilestoneDependency',
      rollback: () => set((st) => ({
        milestoneDependencies: st.milestoneDependencies.map((d) => d.id === id ? before : d),
      })),
    });
  },

  deleteMilestoneDependency: async (id) => {
    const before = get().milestoneDependencies.find((d) => d.id === id);
    if (!before) return;
    const successor = get().milestones.find((m) => m.id === before.successorId);
    if (successor && milestoneCycleBlocked(get(), successor.rockId)) return;

    set((s) => ({ milestoneDependencies: s.milestoneDependencies.filter((d) => d.id !== id) }));
    applyWrite(coreApi(`/records/milestone-dependencies/${id}`, { unlocked_cycle: get().unlockedCycleKey }, 'DELETE'), {
      noun: 'this dependency', context: 'deleteMilestoneDependency', operation: 'delete',
      rollback: () => set((st) => ({ milestoneDependencies: [...st.milestoneDependencies, before] })),
    });
  },

  toggleMilestone: async (id) => {
    const milestone = get().milestones.find((m) => m.id === id);
    if (!milestone) return;
    if (milestoneCycleBlocked(get(), milestone.rockId)) return;
    const completed = !milestone.completed;

    milestoneMutations.setMilestoneComplete(activeContext(get), milestone, completed, {
      apply: (next, completedAt) => set((s) => ({
        milestones: s.milestones.map((m) => m.id === id ? { ...m, completed: next, completedAt } : m),
      })),
      rollback: () => set((s) => ({
        milestones: s.milestones.map((m) =>
          m.id === id ? { ...m, completed: !completed, completedAt: milestone.completedAt } : m),
      })),
    });
  },

  addRockUpdate: async ({ rockId, message, statusAtUpdate, isSystemGenerated }) => {
    let createdId: string | undefined;
    await rockMutations.addRockUpdate(
      activeContext(get),
      { rockId, message, statusAtUpdate, isSystemGenerated },
      {
        apply: (update) => {
          createdId = update.id;
          set((s) => ({ rockUpdates: [update, ...s.rockUpdates] }));
        },
        rollback: () => set((s) => ({ rockUpdates: s.rockUpdates.filter((u) => u.id !== createdId) })),
        rock: get().rocks.find((r) => r.id === rockId),
        setStatus: (status) => get().updateRock(rockId, { status }, { skipStatusRecord: true }),
      },
    );
  },

  editRockUpdate: async (id, message) => {
    const editedAt = new Date().toISOString();
    set((s) => ({
      rockUpdates: s.rockUpdates.map((u) =>
        u.id === id ? { ...u, message, editedAt } : u
      ),
    }));
    await coreApi(`/records/rock-updates/${id}`, { message, edited_at: editedAt }, 'PATCH');
  },

  deleteRockUpdate: async (id) => {
    set((s) => ({ rockUpdates: s.rockUpdates.filter((u) => u.id !== id) }));
    await coreApi(`/records/rock-updates/${id}`, {}, 'DELETE');
  },

  // Metrics
  addMetric: async (metric) => {
    const { workspaceId } = get();
    const finalMetric = { ...metric, teamId: metric.teamId ?? newEntityTeamId(get()) };
    set((s) => ({ metrics: [...s.metrics, finalMetric] }));
    const { data: sent, error } = await coreApi('/records/metrics', metricInsertRow(finalMetric, workspaceId));
    const data = sent?.[0] as Record<string, unknown> | undefined;
    if (error) {
      console.error('[addMetric]', error);
      set((s) => ({ metrics: s.metrics.filter((m) => m.id !== metric.id) }));
      storeNotice.error('That metric could not be created.');
      return false;
    }
    // An aggregate's owner, team, indicator, and formatting are set by the source,
    // not the caller, so the committed row is the truth here.
    if (data) {
      const stored = metricFromRow(data);
      set((s) => ({ metrics: s.metrics.map((m) => (m.id === stored.id ? stored : m)) }));
    }
    return true;
  },
  addCatalogMetrics: async ({ create, links }) => {
    const { workspaceId } = get();
    for (const { metric, catalogKey } of links) {
      const { data, error } = await coreApi(`/records/metrics/${metric.id}`, { catalog_key: catalogKey }, 'PATCH');
      if (error || !data?.length) {
        console.error('[addCatalogMetrics]', error);
        storeNotice.error(`${metric.title} could not be linked to the catalog.`);
        return false;
      }
      set((s) => ({ metrics: s.metrics.map((m) => (m.id === metric.id ? { ...m, catalogKey } : m)) }));
    }
    if (!create.length) return true;
    const finals = create.map((metric) => ({ ...metric, teamId: metric.teamId ?? newEntityTeamId(get()) }));
    const createdIds = new Set(finals.map((metric) => metric.id));
    set((s) => ({ metrics: [...s.metrics, ...finals] }));
    const { data: sent, error } = await coreApi('/records/metrics/batch', {
      rows: finals.map((metric) => metricInsertRow(metric, workspaceId)),
    });
    const data = sent?.[0] as { id: string }[] | undefined;
    if (error || !data) {
      console.error('[addCatalogMetrics]', error);
      set((s) => ({ metrics: s.metrics.filter((m) => !createdIds.has(m.id)) }));
      storeNotice.error('Those metrics could not be created.');
      return false;
    }
    const stored = new Map(data.map((row) => [row.id, metricFromRow(row)]));
    set((s) => ({ metrics: s.metrics.map((m) => stored.get(m.id) ?? m) }));
    return true;
  },
  updateMetric: (id, requested) => {
    const before = get().metrics.find((k) => k.id === id);
    const touchesDefinition = ['cadence', 'origin', 'formulaExpression', 'aggregate'].some((field) => field in requested);
    const updates: Partial<Metric> = before?.catalogKey && touchesDefinition && !('catalogKey' in requested)
      && !keepsCatalogKey({ ...before, ...requested }, get().metrics)
      ? { ...requested, catalogKey: undefined }
      : requested;
    const changes = before
      ? diffFields(before, updates, ['title', 'unit', 'origin', 'indicator', 'cadence', 'formulaExpression', 'aggregate', 'startPeriod', 'openingValue', 'catalogKey', 'targets', 'ownerId'])
      : [];
    const rescales = !!before && 'unit' in updates && (before.unit === '%') !== (updates.unit === '%');
    const rescaled = (metric: Metric): Metric => {
      if (!rescales || (updates.origin ?? metric.origin) !== 'input') return metric;
      const factor = updates.unit === '%' ? 0.01 : 100;
      const scale = (value: number) => Number((value * factor).toPrecision(12));
      return {
        ...metric,
        entries: metric.entries.map((entry) => (entry.value === null ? entry : { ...entry, value: scale(entry.value) })),
        targets: metric.targets.map((target) => ({
          ...target, target: scale(target.target), ...(target.targetHigh == null ? {} : { targetHigh: scale(target.targetHigh) }),
        })),
      };
    };
    set((s) => ({ metrics: s.metrics.map((k) => k.id === id ? rescaled({ ...k, ...updates }) : k) }));
    const dbUpdates: any = {};
    if (updates.title !== undefined) dbUpdates.title = updates.title;
    if ('unit' in updates) dbUpdates.unit = updates.unit ?? null;
    if (updates.decimals !== undefined) dbUpdates.decimals = updates.decimals ?? null;
    if (updates.ownerId !== undefined) dbUpdates.owner_id = updates.ownerId ?? null;
    if (updates.goalIds !== undefined) dbUpdates.goal_ids = updates.goalIds;
    if (updates.origin !== undefined) dbUpdates.origin = updates.origin;
    if ('formulaExpression' in updates) dbUpdates.formula_expression = updates.formulaExpression ?? null;
    if ('aggregate' in updates) {
      dbUpdates.aggregate_source_metric_id = updates.aggregate?.sourceMetricId ?? null;
      dbUpdates.aggregate_function = updates.aggregate?.function ?? null;
      dbUpdates.aggregate_trailing_periods = updates.aggregate?.trailingPeriods ?? null;
      dbUpdates.aggregate_incomplete_policy = updates.aggregate?.incompletePolicy ?? null;
    }
    if ('indicator' in updates) dbUpdates.indicator = updates.indicator ?? null;
    if ('startPeriod' in updates) dbUpdates.start_period = updates.startPeriod ?? null;
    if ('openingValue' in updates) dbUpdates.opening_value = updates.openingValue ?? null;
    if ('catalogKey' in updates) dbUpdates.catalog_key = updates.catalogKey ?? null;
    if (updates.cadence !== undefined) dbUpdates.cadence = updates.cadence;
    if (updates.rockIds !== undefined) dbUpdates.rock_ids = updates.rockIds;
    if (updates.targets !== undefined) dbUpdates.targets = updates.targets;
    if (Object.keys(dbUpdates).length) {
      applyWrite(coreApi(`/records/metrics/${id}`, dbUpdates, 'PATCH'), {
        noun: 'this metric',
        context: 'updateMetric',
        rollback: () => {
          if (before) set((st) => ({ metrics: st.metrics.map((x) => (x.id === id ? before : x)) }));
        },
        onSuccess: () => {
          if ('aggregate' in updates || updates.origin === 'aggregate' || rescales) refetchDomain(get)();
        },
      });
    }
  },
  deleteMetric: async (id) => {
    const before = get().metrics.find((k) => k.id === id);
    const { data: sent, error } = await coreApi(`/metrics/${id}`, {}, 'DELETE');
    const data = (sent?.[0] as { deleted?: { id: string }[] } | undefined)?.deleted;
    if (error || !data) {
      console.error('[deleteMetric]', error);
      storeNotice.error('That metric could not be deleted.');
      return;
    }
    const deletedIds = new Set(data.map((row) => row.id));
    set((s) => ({ metrics: s.metrics.filter((metric) => !deletedIds.has(metric.id)) }));
  },
  addMetricEntry: (metricId, period, value) => {
    const before = get().metrics.find((k) => k.id === metricId);
    if (!before || before.origin !== 'input') {
      storeNotice.error('Only Input metrics accept entered values.');
      return;
    }
    const ws = useWorkspaceStore.getState().settings;
    // Only the `metrics` slice, as before. `myMetrics` is fetched separately and
    // has never been patched by an entry write.
    const patch = (entries: Metric['entries']) => set((s) => ({
      metrics: s.metrics.map((k) => k.id === metricId ? { ...k, entries } : k),
    }));
    metricMutations.setMetricEntry(activeContext(get), metricId, period, value, before, {
      apply: patch,
      reconcile: patch,
      thresholds: workspaceMetricThresholds(ws),
    });
  },

  // Scorecard CRUD
  addScorecard: async (scorecard) => {
    const { workspaceId } = get();
    const teamId = scorecard.teamId ?? newEntityTeamId(get());
    // No primacy handling here. The board a team opens on lives on the SPACE
    // (`teams.primary_scorecard_id`), and `adopt_first_scorecard_as_primary`
    // claims it when the team has none — in the database, because create,
    // clone and import are three paths and a rule in one of them is a rule the
    // other two lack.
    set((s) => ({ scorecards: [...s.scorecards, { ...scorecard, teamId }] }));
    const { error } = await coreApi('/records/scorecards', {
      ...(teamId ? { team_id: teamId } : {}),
      id: scorecard.id,
      workspace_id: workspaceId,
      name: scorecard.name,
    });
    if (error) {
      console.error('[addScorecard]', error);
      set((s) => ({ scorecards: s.scorecards.filter((candidate) => candidate.id !== scorecard.id) }));
      storeNotice.error('That scorecard could not be created.');
      return false;
    }
    return true;
  },
  updateScorecard: (id, updates) => {
    set((s) => ({
      scorecards: s.scorecards.map((sc) => sc.id === id ? { ...sc, ...updates } : sc),
    }));
    const dbUpdates: any = {};
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (Object.keys(dbUpdates).length)
      applyWrite(coreApi(`/records/scorecards/${id}`, dbUpdates, 'PATCH'),
        { noun: 'this scorecard', context: 'updateScorecard', refetch: refetchDomain(get) });
  },
  archiveScorecard: async (id) => {
    const before = get().scorecards.find((sc) => sc.id === id);
    // Status is the whole visible change, so it is applied locally and left
    // there. The realtime UPDATE carries the server's stamps in behind it; a
    // refetch here would blank and refill every slice to learn nothing.
    set((s) => ({ scorecards: s.scorecards.map((sc) => sc.id === id ? { ...sc, status: 'archived' as const, archivedAt: new Date().toISOString() } : sc) }));
    const { error } = await coreApi(`/scorecards/${id}/archive`);
    if (error) {
      console.error('[archiveScorecard]', error);
      set((s) => ({ scorecards: s.scorecards.map((sc) => sc.id === id && before ? before : sc) }));
      // The database refuses a board a team opens on, and that refusal is the
      // one a manager will actually hit, so it gets its own sentence.
      storeNotice.error(/primary first/.test(`${error.message} ${error.details}`)
        ? 'Make another scorecard primary before archiving this one.'
        : 'That scorecard could not be archived.');
      return;
    }
  },
  restoreScorecard: async (id) => {
    const before = get().scorecards.find((sc) => sc.id === id);
    set((s) => ({ scorecards: s.scorecards.map((sc) => sc.id === id ? { ...sc, status: 'active' as const, archivedAt: null, archivedBy: null } : sc) }));
    const { error } = await coreApi(`/scorecards/${id}/restore`);
    if (error) {
      console.error('[restoreScorecard]', error);
      set((s) => ({ scorecards: s.scorecards.map((sc) => sc.id === id && before ? before : sc) }));
      storeNotice.error('That scorecard could not be restored.');
      return;
    }
  },
  deleteScorecard: async (id) => {
    const before = get().scorecards.find((sc) => sc.id === id);
    const beforeMemberships = get().scorecardMetrics.filter((sm) => sm.scorecardId === id);
    set((s) => ({
      scorecards: s.scorecards.filter((sc) => sc.id !== id),
      scorecardMetrics: s.scorecardMetrics.filter((sm) => sm.scorecardId !== id),
    }));
    const { error } = await coreApi(`/scorecards/${id}`, {}, 'DELETE');
    if (error) {
      console.error('[deleteScorecard]', error);
      if (before) set((s) => ({
        scorecards: [...s.scorecards, before],
        scorecardMetrics: [...s.scorecardMetrics, ...beforeMemberships],
      }));
      storeNotice.error('That scorecard could not be deleted.');
      return;
    }
  },
  cloneScorecard: (sourceId, newName) => {
    const { workspaceId, scorecardMetrics } = get();
    const source = get().scorecards.find((sc) => sc.id === sourceId);
    if (!source) return;
    const newId = crypto.randomUUID();
    // The fourth place on the team-scoping contract, and it was empty: the
    // INSERT below stamps a team and the optimistic row did not, so a clone
    // read as a root board until the next load — wherever it was actually made.
    const teamId = newEntityTeamId(get());
    const newScorecard: Scorecard = {
      id: newId,
      workspaceId: workspaceId!,
      teamId,
      name: newName,
      createdAt: new Date().toISOString(),
      status: 'active',
    };
    set((s) => ({ scorecards: [...s.scorecards, newScorecard] }));

    const memberships = scorecardMetrics.filter((sm) => sm.scorecardId === sourceId);
    const newMemberships: ScorecardMetric[] = memberships.map((sm) => ({
      id: crypto.randomUUID(),
      scorecardId: newId,
      metricId: sm.metricId,
    }));
    if (newMemberships.length) {
      set((s) => ({ scorecardMetrics: [...s.scorecardMetrics, ...newMemberships] }));
    }

    // `cloned_from_id` is what the materialisation and notification triggers
    // read (#165). Cloning a board shared to this team turns the copy's metrics
    // from a lease into shares of its own; cloning an owned board does nothing.
    void coreApi('/records/scorecards', {
      ...(teamId ? { team_id: teamId } : {}),
      id: newId, workspace_id: workspaceId, name: newName,
      cloned_from_id: sourceId,
    }).then(async ({ error }) => {
      if (error) {
        console.error('[cloneScorecard] failed', error.message);
        set((s) => ({
          scorecards: s.scorecards.filter((sc) => sc.id !== newId),
          scorecardMetrics: s.scorecardMetrics.filter((sm) => sm.scorecardId !== newId),
        }));
        storeNotice.error(`${error.message} ${error.details}`.includes('does not allow copies')
          ? 'The team that shared this board does not allow copies of it.'
          : 'That board could not be copied.');
        return;
      }

      const { data: copied } = await supabase.from('scorecard_metrics').select('id, scorecard_id, metric_id').eq('scorecard_id', newId);
      if (copied) {
        set((s) => ({
          scorecardMetrics: [
            ...s.scorecardMetrics.filter((sm) => sm.scorecardId !== newId),
            ...copied.map((r) => ({ id: r.id, scorecardId: r.scorecard_id, metricId: r.metric_id })),
          ],
        }));
      }

      // The trigger materialised shares of the source's metrics, and those
      // metrics are homed in the OTHER team — so neither the shares nor the
      // metric rows are on this team's store yet.
      await useTeamsStore.getState().refreshShares();
      const wanted = newMemberships.map((m) => m.metricId)
        .filter((id) => !get().metrics.some((m) => m.id === id));
      if (!wanted.length) return;
      const { data } = await supabase.from('metrics').select(METRIC_SELECT).in('id', wanted);
      if (data?.length) {
        set((s) => ({
          metrics: [...s.metrics, ...(data as any[]).map(metricFromRow)
            .filter((m) => !s.metrics.some((x) => x.id === m.id))],
        }));
      }
    });

    return newId;
  },

  // Scorecard metric membership
  addScorecardMetric: async (qsk) => {
    const { workspaceId, metrics } = get();
    const metric = metrics.find((k) => k.id === qsk.metricId);
    set((s) => ({ scorecardMetrics: [...s.scorecardMetrics, qsk] }));
    const { data, error } = await coreApi('/records/scorecard-metrics', {
      id: qsk.id,
      scorecard_id: qsk.scorecardId,
      metric_id: qsk.metricId,
      workspace_id: workspaceId,
    });
    if (error || !data?.length) {
      console.error('[addScorecardMetric]', error);
      set((s) => ({ scorecardMetrics: s.scorecardMetrics.filter((sm) => sm.id !== qsk.id) }));
      storeNotice.error(error?.code === '23505'
        ? 'That metric is already tracked on this board.'
        : 'That metric could not be added to this board.');
      return false;
    }
    return true;
  },
  removeScorecardMetric: (id) => {
    const before = get().scorecardMetrics.find((q) => q.id === id);
    const beforeMetric = before ? get().metrics.find((k) => k.id === before.metricId) : undefined;
    set((s) => ({ scorecardMetrics: s.scorecardMetrics.filter((q) => q.id !== id) }));
    applyWrite(coreApi(`/records/scorecard-metrics/${id}`, {}, 'DELETE'), {
      noun: 'this metric',
      context: 'removeScorecardMetric',
      operation: 'delete',
      refetch: refetchDomain(get),
    });
  },

  // Actions
  addTask: (item) => {
    taskMutations.addTask(activeContext(get), item, {
      apply: (task) => set((s) => ({ tasks: [...s.tasks, task] })),
      rollback: () => set((st) => ({ tasks: st.tasks.filter((t) => t.id !== item.id) })),
      unassign: () => set((st) => ({
        tasks: st.tasks.map((t) => (t.id === item.id ? { ...t, ownerId: undefined } : t)),
      })),
    });
  },
  updateTask: (id, updates) => {
    // `myTasks` is fetched separately and is not a subset of `tasks`: a to-do of
    // mine on another team is in one and not the other. Falling back to it is
    // what gives the cycle guard, the audit entry and the notification a row to
    // read when Home is the only surface holding this to-do.
    const before = get().tasks.find((a) => a.id === id) ?? get().myTasks.find((a) => a.id === id);
    const rock = before?.rockId ? get().rocks.find((r) => r.id === before.rockId) : undefined;
    taskMutations.updateTask(activeContext(get), id, updates, before, {
      // Both slices, or Home's card writes to the database and never redraws.
      apply: (patch) => set((s) => ({
        tasks: s.tasks.map((a) => a.id === id ? { ...a, ...patch } : a),
        myTasks: s.myTasks.map((a) => a.id === id ? { ...a, ...patch } : a),
      })),
      rollback: () => {
        if (before) set((st) => ({
          tasks: st.tasks.map((x) => (x.id === id ? before : x)),
          myTasks: st.myTasks.map((x) => (x.id === id ? before : x)),
        }));
      },
      rockOwnerId: rock?.ownerId,
    });
  },
  deleteTask: (id) => {
    const before = get().tasks.find((a) => a.id === id) ?? get().myTasks.find((a) => a.id === id);
    taskMutations.deleteTask(activeContext(get), id, before, {
      apply: () => set((s) => ({
        tasks: s.tasks.filter((a) => a.id !== id),
        myTasks: s.myTasks.filter((a) => a.id !== id),
      })),
    });
  },

  // Issues
  addIssue: (issue) => {
    const ctx = activeContext(get);
    if (ctx.isBlocked(issue.year, issue.quarter)) return;
    // Owner priority: explicit > personal-workspace owner > current user's linked person
    const finalIssue = issueMutations.prepareIssue(ctx, issue);
    set((s) => ({ issues: [...s.issues, finalIssue] }));
    const rollback = () => set((st) => ({ issues: st.issues.filter((i) => i.id !== finalIssue.id) }));
    const doInsert = (ownerId: string | null | undefined) => {
      issueMutations.insertIssue(ctx, { ...finalIssue, ownerId: ownerId ?? undefined }, { rollback });
    };
    if (finalIssue.ownerId) {
      doInsert(finalIssue.ownerId);
    } else {
      // Resolve current-user person, then insert
      supabase.auth.getUser().then(({ data }) => {
        const ws = useWorkspaceStore.getState();
        const me = ws.members.find((m) => m.userId === data.user?.id && m.personId);
        const ownerId = me?.personId ?? null;
        if (ownerId) {
          set((s) => ({ issues: s.issues.map((i) => i.id === finalIssue.id ? { ...i, ownerId } : i) }));
        }
        doInsert(ownerId);
      }).catch((error) => {
        // The only step between the optimistic row and its insert. If it fails
        // the insert never happens at all, so the row has to come back off
        // rather than sit there looking saved.
        console.error('[addIssue] owner lookup failed:', error);
        set((st) => ({ issues: st.issues.filter((i) => i.id !== finalIssue.id) }));
        storeNotice.error("Couldn't save this issue.");
      });
    }
  },
  updateIssue: (id, updates) => {
    const before = get().issues.find((i) => i.id === id) ?? get().myIssues.find((i) => i.id === id);
    if (before && blockedByCycle(get(), before.year, before.quarter)) return;
    // If caller toggles `resolved` or `status`, keep both in sync
    const patch: any = { ...updates };
    if (patch.status !== undefined && patch.resolved === undefined) patch.resolved = patch.status === 'resolved';
    if (patch.resolved !== undefined && patch.status === undefined) patch.status = patch.resolved ? 'resolved' : 'open';
    set((s) => ({
      issues: s.issues.map((i) => i.id === id ? { ...i, ...patch } : i),
      myIssues: s.myIssues.map((i) => i.id === id ? { ...i, ...patch } : i),
    }));
    const dbUpdates: any = {};
    if (patch.title !== undefined) dbUpdates.title = patch.title;
    if (patch.description !== undefined) dbUpdates.description = patch.description;
    if (patch.ownerId !== undefined) dbUpdates.owner_id = patch.ownerId;
    if (patch.priority !== undefined) dbUpdates.priority = patch.priority;
    if (patch.resolved !== undefined) dbUpdates.resolved = patch.resolved;
    if (patch.status !== undefined) dbUpdates.status = patch.status;
    if (patch.resolvedAt !== undefined) dbUpdates.resolved_at = patch.resolvedAt;
    if (patch.year !== undefined) dbUpdates.year = patch.year ?? null;
    if (patch.quarter !== undefined) dbUpdates.quarter = patch.quarter ?? null;
    if (patch.rockId !== undefined) dbUpdates.rock_id = patch.rockId ?? null;
    if (patch.queuedAction !== undefined) dbUpdates.queued_action = patch.queuedAction ?? null;
    if (Object.keys(dbUpdates).length) {
      applyWrite(coreApi(`/records/issues/${id}`, { ...dbUpdates, unlocked_cycle: get().unlockedCycleKey }, 'PATCH'), {
        noun: 'this issue',
        context: 'updateIssue',
        rollback: () => {
          if (before) set((st) => ({
            issues: st.issues.map((x) => (x.id === id ? before : x)),
            myIssues: st.myIssues.map((x) => (x.id === id ? before : x)),
          }));
        },
      });
    }
  },
  deleteIssue: (id) => {
    const before = get().issues.find((i) => i.id === id) ?? get().myIssues.find((i) => i.id === id);
    issueMutations.deleteIssue(activeContext(get), id, before, {
      apply: () => set((s) => ({
        issues: s.issues.filter((i) => i.id !== id),
        myIssues: s.myIssues.filter((i) => i.id !== id),
      })),
    });
  },

  queueIssueAction: (id, action) => {
    set((s) => ({
      issues: s.issues.map((i) => i.id === id ? { ...i, queuedAction: action ?? undefined } : i),
      myIssues: s.myIssues.map((i) => i.id === id ? { ...i, queuedAction: action ?? undefined } : i),
    }));
    applyWrite(coreApi(`/records/issues/${id}`, { queued_action: action ?? null, unlocked_cycle: get().unlockedCycleKey }, 'PATCH'),
      { noun: 'this issue', context: 'queueIssueAction', refetch: refetchDomain(get) });
  },

  queueTaskAction: (id, action) => {
    taskMutations.queueTaskAction(activeContext(get), id, action, {
      apply: () => set((s) => ({
        tasks: s.tasks.map((a) => a.id === id ? { ...a, queuedAction: action ?? undefined } : a),
        myTasks: s.myTasks.map((a) => a.id === id ? { ...a, queuedAction: action ?? undefined } : a),
      })),
    });
  },

  commitQueuedStates: async () => {
    const { issues, tasks, updateIssue, deleteIssue, addIdea, updateTask, deleteTask } = get();
    const existingIdeas = get().ideas;

    for (const issue of issues) {
      if (!issue.queuedAction) continue;
      if (issue.queuedAction === 'resolve') {
        updateIssue(issue.id, { status: 'resolved', resolvedAt: new Date().toISOString().split('T')[0], queuedAction: null });
      } else if (issue.queuedAction === 'to-idea') {
        // Dedup: skip idea creation if an open idea for this issue already exists
        const existing = existingIdeas.find((i) => i.sourceIssueId === issue.id && i.status === 'open');
        if (!existing) {
          const targetQuarter = issue.year != null && issue.quarter != null
            ? `${issue.year}-Q${issue.quarter}`
            : undefined;
          await addIdea({ title: issue.title, description: issue.description, sourceIssueId: issue.id, targetQuarter });
        }
        updateIssue(issue.id, { status: 'archived', queuedAction: null });
      } else if (issue.queuedAction === 'delete') {
        deleteIssue(issue.id);
      }
    }

    for (const item of tasks) {
      if (!item.queuedAction || item.queuedAction === 'delay') continue;
      if (item.queuedAction === 'complete') {
        updateTask(item.id, { status: 'done', queuedAction: null });
      } else if (item.queuedAction === 'discuss') {
        updateTask(item.id, { status: 'discussed', queuedAction: null });
      } else if (item.queuedAction === 'delete') {
        deleteTask(item.id);
      }
    }
  },

  // Team
  addPerson: (member) => {
    const { workspaceId, people } = get();
    const badgeColor = pickBadgeColor(member.initials, people);
    const memberWithColor = { ...member, badgeColor };
    set((s) => ({ people: [...s.people, memberWithColor].sort((a, b) => a.name.localeCompare(b.name)) }));
    return applyWrite(coreApi('/records/people', {
      id: member.id, name: member.name, initials: member.initials,
      role: member.role ?? null,
      workspace_id: workspaceId,
      badge_color: badgeColor,
    }), {
      noun: 'this person',
      context: 'addPerson',
      rollback: () => set((st) => ({ people: st.people.filter((m) => m.id !== member.id) })),
    });
  },
  removePerson: (id) => {
    set((s) => ({ people: s.people.filter((m) => m.id !== id) }));
    const written = applyWrite(coreApi(`/records/people/${id}`, {}, 'DELETE'), { noun: 'this person', context: 'removePerson', operation: 'delete', refetch: refetchDomain(get) });
    const wsId = get().workspaceId;
    if (wsId) useStakeholderStore.getState().purgePerson(id, wsId);
    return written;
  },
  updatePerson: (id, updates) => {
    const state = get();
    const before = state.people.find((m) => m.id === id);
    const finalUpdates = { ...updates };

    // Re-assign badge color when initials change and the current color conflicts
    if (updates.initials && updates.initials !== before?.initials) {
      const otherMembers = state.people.filter((m) => m.id !== id);
      const conflicts = otherMembers.some(
        (m) => m.initials === updates.initials && m.badgeColor === before?.badgeColor,
      );
      if (conflicts || !before?.badgeColor) {
        finalUpdates.badgeColor = pickBadgeColor(updates.initials, otherMembers);
      }
    }

    set((s) => ({ people: s.people.map((m) => m.id === id ? { ...m, ...finalUpdates } : m).sort((a, b) => a.name.localeCompare(b.name)) }));
    const dbUpdates: any = {};
    if (finalUpdates.name !== undefined) dbUpdates.name = finalUpdates.name;
    if (finalUpdates.initials !== undefined) dbUpdates.initials = finalUpdates.initials;
    if (finalUpdates.role !== undefined) dbUpdates.role = finalUpdates.role ?? null;
    if (finalUpdates.archived !== undefined) dbUpdates.archived = finalUpdates.archived;
    if (finalUpdates.featured !== undefined) dbUpdates.featured = finalUpdates.featured;
    if (finalUpdates.badgeColor !== undefined) dbUpdates.badge_color = finalUpdates.badgeColor;
    const written = Object.keys(dbUpdates).length
      ? applyWrite(coreApi(`/records/people/${id}`, dbUpdates, 'PATCH'), {
        noun: 'this person',
        context: 'updatePerson',
        rollback: () => {
          if (before) set((st) => ({ people: st.people.map((x) => (x.id === id ? before : x)) }));
        },
      })
      : Promise.resolve(true);
    return written;
  },

  // Meetings
  addMeeting: (meeting) => {
    const { workspaceId } = get();
    set((s) => ({ meetings: [...s.meetings, { ...meeting, teamId: meeting.teamId ?? newEntityTeamId(get()) }] }));
    const createdTeamId = newEntityTeamId(get()) ?? null;
    applyWrite(coreApi('/records/meetings', {
      team_id: createdTeamId,
      id: meeting.id,
      year: meeting.year ?? null, quarter: meeting.quarter ?? null,
      date: meeting.date,
      data_period_week: meeting.dataPeriodWeek,
      host_id: meeting.hostId ?? null,
      target_length_minutes: meeting.targetLengthMinutes,
      actual_length_minutes: meeting.actualLengthMinutes ?? null,
      average_score: meeting.averageScore ?? null,
      report_monthly: meeting.reportMonthly,
      meeting_type: meeting.meetingType ?? 'weekly',
      section_targets: meeting.sectionTargets as any,
      section_elapsed_map: (meeting.sectionElapsedMap ?? null) as any,
      resolved_issue_ids: meeting.resolvedIssueIds,
      created_task_ids: meeting.createdTaskIds,
      unresolved_issue_ids: meeting.unresolvedIssueIds ?? [],
      notes: meeting.notes ?? null,
      issue_notes: meeting.issueNotes ?? {},
      rock_snapshot: meeting.rockSnapshot
        ? meeting.rockSnapshot.map((s) => ({
            rock_id: s.rockId,
            title: s.title,
            status: s.status,
            owner_id: s.ownerId ?? null,
          }))
        : null,
      fathom_summaries: serializeFathomSummaries(meeting.fathomSummaries ?? []),
      status: meeting.status ?? 'complete',
      session_count: meeting.sessionCount ?? 1,
      scheduled_at: meeting.scheduledAt ?? null,
      meeting_schedule_id: meeting.meetingScheduleId ?? null,
      occurrence_start: meeting.occurrenceStart ?? null,
      attendees: meeting.attendees as any,
      invitees: meeting.invitees ?? [],
      notification_timing_hours: meeting.notificationTimingHours ?? null,
      prep_email_enabled: meeting.prepEmailEnabled ?? null,
      agenda: meeting.agenda ?? null,
      section_data: meeting.sectionData ?? null,
      workspace_id: workspaceId,
    }), {
      noun: 'this meeting',
      context: 'addMeeting',
      rollback: () => set((st) => ({ meetings: st.meetings.filter((m) => m.id !== meeting.id) })),
    });
  },
  // Merged against a fresh read rather than the copy the caller loaded: the
  // check-in synthesis writes a different key of the same `section_data`
  // server-side, and a dialog left open across that run would otherwise put
  // back the version it opened with (#382 §5).
  updateMeetingQuarterlySetup: async (id, setup) => {
    const { data, error } = await supabase
      .from('meetings').select('section_data').eq('id', id).maybeSingle();
    if (error) {
      applyWrite(Promise.resolve({ data: null, error }) as never,
        { noun: 'this meeting', context: 'updateMeetingQuarterlySetup' });
      return;
    }
    const current = ((data as { section_data?: Record<string, unknown> } | null)?.section_data
      ?? {}) as Record<string, unknown>;
    const next = {
      ...current,
      'quarterly-setup': { ...(current['quarterly-setup'] as Record<string, unknown> ?? {}), ...setup },
    };
    set((s) => ({
      meetings: s.meetings.map((m) => m.id === id ? { ...m, sectionData: next } : m),
    }));
    applyWrite(coreApi(`/records/meetings/${id}`, { section_data: next }, 'PATCH'),
      { noun: 'this meeting', context: 'updateMeetingQuarterlySetup', refetch: refetchDomain(get) });
  },

  updateMeeting: (id, updates, opts) => {
    const before = get().meetings.find((m) => m.id === id);
    set((s) => ({
      meetings: s.meetings.map((m) => m.id === id ? { ...m, ...updates } : m),
    }));
    const dbUpdates: any = {};
    if (updates.date !== undefined) dbUpdates.date = updates.date;
    if (updates.year !== undefined) dbUpdates.year = updates.year;
    if (updates.quarter !== undefined) dbUpdates.quarter = updates.quarter;
    if (updates.dataPeriodWeek !== undefined) dbUpdates.data_period_week = updates.dataPeriodWeek;
    if (updates.hostId !== undefined) dbUpdates.host_id = updates.hostId;
    if (updates.targetLengthMinutes !== undefined) dbUpdates.target_length_minutes = updates.targetLengthMinutes;
    if (updates.actualLengthMinutes !== undefined) dbUpdates.actual_length_minutes = updates.actualLengthMinutes;
    if (updates.averageScore !== undefined) dbUpdates.average_score = updates.averageScore;
    if (updates.reportMonthly !== undefined) dbUpdates.report_monthly = updates.reportMonthly;
    if (updates.sectionTargets !== undefined) dbUpdates.section_targets = updates.sectionTargets;
    if (updates.sectionElapsedMap !== undefined) dbUpdates.section_elapsed_map = updates.sectionElapsedMap ?? null;
    if (updates.resolvedIssueIds !== undefined) dbUpdates.resolved_issue_ids = updates.resolvedIssueIds;
    if (updates.createdTaskIds !== undefined) dbUpdates.created_task_ids = updates.createdTaskIds;
    if (updates.notes !== undefined) dbUpdates.notes = updates.notes ?? null;
    if (updates.meetingType !== undefined) dbUpdates.meeting_type = updates.meetingType;
    if (updates.fathomSummaries !== undefined) dbUpdates.fathom_summaries = serializeFathomSummaries(updates.fathomSummaries);
    if (updates.issueNotes !== undefined) dbUpdates.issue_notes = updates.issueNotes ?? {};
    if (updates.status !== undefined) dbUpdates.status = updates.status;
    if (updates.sessionCount !== undefined) dbUpdates.session_count = updates.sessionCount;
    if (updates.draftState !== undefined) dbUpdates.draft_state = updates.draftState ?? null;
    if (updates.unresolvedIssueIds !== undefined) dbUpdates.unresolved_issue_ids = updates.unresolvedIssueIds ?? [];
    if (updates.scheduledAt !== undefined) dbUpdates.scheduled_at = updates.scheduledAt ?? null;
    // Written together or not at all — the pair constraint refuses a half-set
    // occurrence, and a series edit that moved only `scheduled_at` would mark
    // every instance it touched as deliberately moved (#411 D6).
    if (updates.occurrenceStart !== undefined) {
      dbUpdates.occurrence_start = updates.occurrenceStart ?? null;
      dbUpdates.meeting_schedule_id = updates.meetingScheduleId ?? null;
    }
    if (updates.attendees !== undefined) dbUpdates.attendees = updates.attendees;
    if (updates.agenda !== undefined) dbUpdates.agenda = updates.agenda ?? null;
    if (updates.invitees !== undefined) dbUpdates.invitees = updates.invitees;
    if (updates.sectionData !== undefined) dbUpdates.section_data = updates.sectionData ?? null;
    if (updates.notificationTimingHours !== undefined) dbUpdates.notification_timing_hours = updates.notificationTimingHours ?? null;
    if (updates.prepEmailEnabled !== undefined) dbUpdates.prep_email_enabled = updates.prepEmailEnabled ?? null;
    if (updates.hostId !== undefined) dbUpdates.host_id = updates.hostId ?? null;
    if (updates.rockSnapshot !== undefined) {
      dbUpdates.rock_snapshot = updates.rockSnapshot
        ? updates.rockSnapshot.map((s) => ({ rock_id: s.rockId, title: s.title, status: s.status, owner_id: s.ownerId ?? null }))
        : null;
    }
    if (Object.keys(dbUpdates).length) {
      applyWrite(coreApi(`/records/meetings/${id}`, {
        ...dbUpdates,
        ...(opts?.audit ? { edit: true } : {}),
        ...(opts?.changed ? { changed: opts.changed } : {}),
      }, 'PATCH'), {
        noun: 'this meeting',
        context: 'updateMeeting',
        refetch: refetchDomain(get),
      });
    }

    // The "next" strategy used to create the following instance here, guarded
    // by a workspace-wide `hasFuture` check that any team's scheduled meeting
    // could satisfy. A schedule materializes its own occurrences now, so
    // completing a meeting creates nothing (#411 D10).
  },
  markMeetingPrepSent: (id) => {
    const sentAt = new Date().toISOString();
    set((s) => ({
      meetings: s.meetings.map((m) => m.id === id ? { ...m, prepEmailSentAt: sentAt } : m),
    }));
  },
  // Deleting a series occurrence writes an EXDATE as well as removing the row.
  // Without it the materializer creates the occurrence again on its next pass,
  // so the delete would simply undo itself within the hour — and that is true
  // of a completed occurrence too, which is why this is not gated on status
  // (#411 D9). A one-off has no series to exclude it from and is a plain
  // delete, as it always was.
  deleteMeeting: (id) => {
    const meeting = get().meetings.find((m) => m.id === id);
    const schedule = meeting?.meetingScheduleId
      ? get().meetingSchedules.find((sc) => sc.id === meeting.meetingScheduleId)
      : undefined;

    set((s) => ({ meetings: s.meetings.filter((m) => m.id !== id) }));

    // Whether the occurrence stays gone, which is what `skipped` records.
    let skipped: Promise<boolean> = Promise.resolve(false);
    if (schedule && meeting?.occurrenceStart) {
      const exdates = [...schedule.exdates, meeting.occurrenceStart];
      set((s) => ({
        meetingSchedules: s.meetingSchedules.map((sc) =>
          sc.id === schedule.id ? { ...sc, exdates } : sc),
      }));
      // Resolved once: awaiting the request twice would send the write twice.
      const exdateWrite = coreApi(`/records/meeting-schedules/${schedule.id}`, { exdates }, 'PATCH');
      skipped = exdateWrite.then(({ data, error }) => !error && !!data?.length, () => false);
      applyWrite(exdateWrite, { noun: 'this occurrence', context: 'deleteMeeting.exdate' });
    }

    void skipped.then(() => applyWrite(coreApi(`/records/meetings/${id}`, {}, 'DELETE'), {
      noun: 'this meeting',
      context: 'deleteMeeting',
      operation: 'delete',
      refetch: refetchDomain(get),
    }));
  },

  addMeetingSchedule: (schedule) => {
    const { workspaceId } = get();
    set((s) => ({ meetingSchedules: [...s.meetingSchedules, schedule] }));
    return applyWrite(
      coreApi('/records/meeting-schedules', {
        id: schedule.id,
        workspace_id: workspaceId,
        ...meetingScheduleToRow(schedule),
      }),
      {
        noun: 'this schedule',
        context: 'addMeetingSchedule',
        onSuccess: () => {
          // Only once the row is there for the materializer to read.
          void get().materializeSchedules();
        },
        rollback: () => set((st) => ({
          meetingSchedules: st.meetingSchedules.filter((sc) => sc.id !== schedule.id),
        })),
      },
    );
  },

  updateMeetingSchedule: (id, updates, opts) => commitSeries(get, set, id, updates, { carry: opts?.carry }),

  materializeSchedules: async () => {
    const { workspaceId, fetchedTeamId } = get();
    if (!workspaceId) return;

    // The same pass the cron runs, scoped to this workspace. Idempotent, so a
    // run racing the hourly one writes nothing twice.
    const { error } = await supabase.functions.invoke('materialize-meeting-occurrences', {
      body: { workspaceId },
    });
    if (error) {
      // Not surfaced: the schedule saved, and the hourly pass will catch up.
      console.error('[materializeSchedules] pass failed:', error);
      return;
    }

    // Only the rows a series owns, merged rather than replaced: a full refetch
    // could roll back an edit made while this was in flight.
    const query = supabase.from('meetings').select('*')
      .eq('workspace_id', workspaceId)
      .not('meeting_schedule_id', 'is', null);
    const { data, error: readErr } = await (fetchedTeamId
      ? query.eq('team_id', fetchedTeamId) : query);
    if (readErr || !data) {
      if (readErr) console.error('[materializeSchedules] read back failed:', readErr);
      return;
    }

    set((s) => {
      const byId = new Map(s.meetings.map((m) => [m.id, m]));
      for (const row of data) byId.set((row as { id: string }).id, meetingFromRow(row));
      return { meetings: [...byId.values()] };
    });
  },

  endMeetingSchedule: (id, moved = 'keep') => {
    const schedule = get().meetingSchedules.find((sc) => sc.id === id);
    if (!schedule) return Promise.resolve(false);
    const now = new Date().toISOString();
    // UNTIL is what stops the occurrences; `ended_at` records that someone
    // ended it rather than it running its course.
    const spec = parseRRule(schedule.rrule);
    if (!spec) return Promise.resolve(false);
    const rrule = buildRRule({ ...spec, until: now.slice(0, 10) });

    return commitSeries(get, set, id, { rrule, endedAt: now }, { moved });
  },

  saveMeetingDraft: async (meeting, draftState) => {
    const { workspaceId } = get();
    const existing = get().meetings.find((m) => m.id === meeting.id);
    const createdTeamId = existing ? undefined : (meeting.teamId ?? newEntityTeamId(get()));
    const draftMeeting = {
      ...meeting, teamId: existing?.teamId ?? createdTeamId, status: 'in-progress' as MeetingStatus, draftState,
    };
    if (existing) {
      set((s) => ({
        meetings: s.meetings.map((m) => m.id === meeting.id ? draftMeeting : m),
      }));
    } else {
      set((s) => ({ meetings: [...s.meetings, draftMeeting] }));
    }
    const { error } = await coreApi(`/meetings/${meeting.id}/save`, {
      ...(createdTeamId ? { team_id: createdTeamId } : {}),
      year: meeting.year ?? null,
      quarter: meeting.quarter ?? null,
      date: meeting.date,
      data_period_week: meeting.dataPeriodWeek,
      host_id: meeting.hostId ?? null,
      target_length_minutes: meeting.targetLengthMinutes,
      actual_length_minutes: null,
      average_score: meeting.averageScore ?? null,
      report_monthly: meeting.reportMonthly,
      meeting_type: meeting.meetingType ?? 'weekly',
      section_targets: meeting.sectionTargets as any,
      resolved_issue_ids: meeting.resolvedIssueIds,
      created_task_ids: meeting.createdTaskIds,
      unresolved_issue_ids: meeting.unresolvedIssueIds ?? [],
      notes: meeting.notes ?? null,
      issue_notes: meeting.issueNotes ?? {},
      rock_snapshot: meeting.rockSnapshot
        ? meeting.rockSnapshot.map((s) => ({ rock_id: s.rockId, title: s.title, status: s.status, owner_id: s.ownerId ?? null }))
        : null,
      fathom_summaries: serializeFathomSummaries(meeting.fathomSummaries ?? []),
      status: 'in-progress',
      session_count: meeting.sessionCount,
      draft_state: draftState as any,
      scheduled_at: meeting.scheduledAt ?? null,
      attendees: meeting.attendees as any,
      invitees: meeting.invitees ?? [],
      agenda: meeting.agenda ?? null,
      section_data: meeting.sectionData ?? null,
      workspace_id: workspaceId,
    });
    if (!error) return true;
    console.error('[saveMeetingDraft] failed', error.message);
    set((s) => ({
      meetings: existing
        ? s.meetings.map((m) => m.id === meeting.id ? existing : m)
        : s.meetings.filter((m) => m.id !== meeting.id),
    }));
    storeNotice.error('That meeting could not be saved.');
    return false;
  },

  deleteMeetingDraft: async (id) => {
    set((s) => ({ meetings: s.meetings.filter((m) => m.id !== id) }));
    await coreApi(`/records/meetings/${id}`, {}, 'DELETE');
  },

  returnMeetingToScheduled: (scheduled) => {
    set((s) => ({ meetings: s.meetings.map((m) => m.id === scheduled.id ? scheduled : m) }));
    applyWrite(coreApi(`/records/meetings/${scheduled.id}`, {
      status: 'scheduled',
      draft_state: null,
      session_count: scheduled.sessionCount ?? 0,
      attendees: scheduled.attendees as any,
      date: scheduled.date,
      data_period_week: scheduled.dataPeriodWeek,
      host_id: scheduled.hostId ?? null,
      target_length_minutes: scheduled.targetLengthMinutes,
      report_monthly: scheduled.reportMonthly,
      section_targets: scheduled.sectionTargets as any,
      resolved_issue_ids: scheduled.resolvedIssueIds,
      created_task_ids: scheduled.createdTaskIds,
      notes: scheduled.notes ?? null,
      section_data: scheduled.sectionData ?? null,
    }, 'PATCH'), {
      noun: 'this meeting',
      context: 'returnMeetingToScheduled',
      refetch: refetchDomain(get),
    });
  },

  attachFathomSummary: async (meetingId, payload) => {
    const meeting = get().meetings.find((m) => m.id === meetingId);
    if (!meeting) return;
    if (meeting.fathomSummaries.length >= meeting.sessionCount) return;
    const { data: { user } } = await supabase.auth.getUser();
    const summary = {
      url: payload.url,
      summary: payload.summary,
      candidateTasks: payload.candidateTasks ?? [],
      attachedAt: new Date().toISOString(),
      attachedByUserId: user?.id,
      source: 'manual' as const,
    };
    const updatedSummaries = [...meeting.fathomSummaries, summary];
    set((s) => ({
      meetings: s.meetings.map((m) =>
        m.id === meetingId ? { ...m, fathomSummaries: updatedSummaries } : m
      ),
    }));
    await coreApi(`/records/meetings/${meetingId}`, {
      fathom_summaries: serializeFathomSummaries(updatedSummaries),
    }, 'PATCH');
    if (payload.inboxRowId) {
      await coreApi(`/records/fathom-recordings/${payload.inboxRowId}`, {
        attached_meeting_id: meetingId, match_method: 'manual-select',
      }, 'PATCH');
    }
    // Fire-and-forget AI analysis (only for finalized meetings)
    if (meeting.status !== 'in-progress') void get().analyzeMeeting(meetingId);
  },

  fetchAndAttachFathomByUrl: async (meetingId, shareUrl) => {
    const { workspaceId } = get();
    if (!workspaceId) throw new Error('No workspace');
    const { data, error } = await supabase.functions.invoke('fetch-fathom-meeting', {
      body: { shareUrl, workspaceId },
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    await get().attachFathomSummary(meetingId, {
      url: data.url,
      summary: data.summary ?? undefined,
      candidateTasks: data.candidateTasks ?? [],
    });
  },

  analyzeMeeting: async (meetingId) => {
    const { workspaceId } = get();
    const meeting = get().meetings.find((m) => m.id === meetingId);
    if (!meeting || meeting.status === 'in-progress') return;
    const combinedSummary = meeting.fathomSummaries.map((s) => s.summary).filter(Boolean).join('\n\n---\n\n');
    if (!combinedSummary || !workspaceId) return;
    set((s) => ({ analyzingMeetingIds: [...s.analyzingMeetingIds, meetingId] }));
    try {

    const resolvedIssues = get().issues
      .filter((i) => meeting.resolvedIssueIds.includes(i.id))
      .map((i) => ({ id: i.id, title: i.title, description: i.description ?? '' }));

    const createdTasks = get().tasks
      .filter((a) => meeting.createdTaskIds.includes(a.id))
      .map((a) => ({ id: a.id, title: a.title }));

    const candidateTasks = meeting.fathomSummaries
      .flatMap((s) => s.candidateTasks)
      .filter((c) => c.status === 'pending')
      .map((c) => ({ id: c.id, title: c.title }));

    const { data: fnData } = await supabase.functions.invoke('analyze-meeting', {
      body: {
        meetingId,
        workspaceId,
        fathomSummary: combinedSummary,
        resolvedIssues,
        existingNotes: meeting.notes ?? '',
        candidateTasks,
        createdTasks,
      },
    });

    // Apply task matches returned by the analyze-meeting function
    const taskMatches: Array<{ candidateId: string; matchedTaskId: string; matchedTaskTitle: string }> =
      fnData?.taskMatches ?? [];
    if (taskMatches.length > 0) {
      const meetingNow = get().meetings.find((m) => m.id === meetingId);
      if (meetingNow && meetingNow.fathomSummaries.length > 0) {
        const updatedSummaries = meetingNow.fathomSummaries.map((s) => ({
          ...s,
          candidateTasks: s.candidateTasks.map((c) => {
            const match = taskMatches.find((tm) => tm.candidateId === c.id);
            return match ? { ...c, matchedTaskId: match.matchedTaskId, matchedTaskTitle: match.matchedTaskTitle } : c;
          }),
        }));
        set((s) => ({
          meetings: s.meetings.map((m) =>
            m.id === meetingId ? { ...m, fathomSummaries: updatedSummaries } : m
          ),
        }));
        await coreApi(`/records/meetings/${meetingId}`, {
          fathom_summaries: serializeFathomSummaries(updatedSummaries),
        }, 'PATCH');
      }
    }

    // Reload the updated meeting fields from DB after the function writes them
    const { data } = await supabase
      .from('meetings')
      .select('issue_notes, notes')
      .eq('id', meetingId)
      .maybeSingle();
    if (data) {
      set((s) => ({
        meetings: s.meetings.map((m) =>
          m.id === meetingId
            ? {
                ...m,
                issueNotes: data.issue_notes && typeof data.issue_notes === 'object' && !Array.isArray(data.issue_notes) && Object.keys(data.issue_notes).length ? (data.issue_notes as any) : m.issueNotes,
                notes: data.notes ?? m.notes,
              }
            : m
        ),
      }));
    }
    } finally {
      set((s) => ({ analyzingMeetingIds: s.analyzingMeetingIds.filter((id) => id !== meetingId) }));
    }
  },

  fetchFathomInboxCandidates: async (meetingId) => {
    const { workspaceId } = get();
    const meeting = get().meetings.find((m) => m.id === meetingId);
    if (!workspaceId || !meeting) return [];

    const meetingTypes = useWorkspaceStore.getState().meetingTypes;
    const typeConfig = meetingTypes.find((t) => t.key === (meeting.meetingType ?? 'weekly'));
    const canonicalTitle = (typeConfig?.fathomMeetingTitle ?? '').toString().trim().toLowerCase();

    const meetingDate = new Date(meeting.date + 'T00:00:00');
    const fromIso = new Date(meetingDate.getTime() - 36 * 3600 * 1000).toISOString();
    const toIso = new Date(meetingDate.getTime() + 36 * 3600 * 1000).toISOString();

    const { data: rows } = await supabase
      .from('fathom_webhook_inbox' as any)
      .select('id, payload, fathom_share_url, recorded_at, meeting_title')
      .eq('workspace_id', workspaceId)
      .is('attached_meeting_id', null)
      .gte('recorded_at', fromIso)
      .lte('recorded_at', toIso);

    if (!rows || rows.length === 0) return [];

    const eligible = (rows as any[]).filter((r) => {
      if (!canonicalTitle) return true;
      const t = (r.meeting_title ?? '').toString().trim().toLowerCase();
      return !t || t === canonicalTitle;
    });

    const actionItemToTitle = (x: any): string => {
      if (x == null) return '';
      if (typeof x === 'string') return x.trim();
      if (typeof x === 'object') {
        const v = x.description ?? x.title ?? x.text ?? x.name ?? x.action ?? x.task;
        if (typeof v === 'string') return v.trim();
      }
      return '';
    };
    const extractItems = (p: any): { title: string; assigneeName?: string; assigneeEmail?: string }[] => {
      const arr = Array.isArray(p?.action_items) ? p.action_items : Array.isArray(p?.tasks) ? p.tasks : [];
      if (arr.length) {
        return arr.map((x: any) => {
          const title = actionItemToTitle(x);
          if (!title) return null;
          let assigneeName: string | undefined;
          let assigneeEmail: string | undefined;
          if (x && typeof x === 'object' && x.assignee && typeof x.assignee === 'object') {
            if (typeof x.assignee.name === 'string' && x.assignee.name.trim()) assigneeName = x.assignee.name.trim();
            if (typeof x.assignee.email === 'string' && x.assignee.email.trim()) assigneeEmail = x.assignee.email.trim();
          }
          return { title, assigneeName, assigneeEmail };
        }).filter(Boolean) as any[];
      }
      const md: string | undefined = p?.summary ?? p?.markdown;
      if (typeof md === 'string') {
        const m = md.match(/##\s*Action\s*Items[\s\S]*?(?=\n##\s|\n*$)/i);
        if (m) {
          return m[0]
            .split('\n')
            .map((l: string) => l.replace(/^[\s\-*\d.)]+/, '').trim())
            .filter((l: string) => l && !/^##/i.test(l))
            .map((title: string) => ({ title }));
        }
      }
      return [];
    };

    return [...eligible]
      .sort((a, b) => {
        const da = Math.abs(new Date(a.recorded_at).getTime() - meetingDate.getTime());
        const db = Math.abs(new Date(b.recorded_at).getTime() - meetingDate.getTime());
        return da - db;
      })
      .map((r) => {
        const payload = r.payload ?? {};
        const url: string = r.fathom_share_url ?? payload.share_url ?? payload.url ?? '';
        const summaryText: string | undefined = payload.summary ?? payload.markdown ?? payload.transcript_summary;
        return {
          id: r.id as string,
          url,
          summary: summaryText,
          meetingTitle: r.meeting_title as string | undefined,
          recordedAt: r.recorded_at as string,
          candidateTasks: extractItems(payload).map((it) => ({
            id: `cand-${crypto.randomUUID().replace(/-/g, '').slice(0, 10)}`,
            title: it.title,
            status: 'pending' as const,
            assigneeName: it.assigneeName,
            assigneeEmail: it.assigneeEmail,
          })),
        };
      });
  },

  decideFathomCandidate: async (meetingId, candidateId, decision) => {
    const meeting = get().meetings.find((m) => m.id === meetingId);
    if (!meeting || meeting.fathomSummaries.length === 0) return;
    const candidate = meeting.fathomSummaries.flatMap((s) => s.candidateTasks).find((c) => c.id === candidateId);
    if (!candidate) return;

    const { data: { user } } = await supabase.auth.getUser();
    const decidedAt = new Date().toISOString();
    let acceptedTaskId: string | undefined = candidate.acceptedTaskId;
    let newStatus: 'pending' | 'accepted' | 'rejected' | 'replaced';

    if (decision.type === 'unset') {
      // If accepted (not replaced), delete the created task.
      if (candidate.status === 'accepted' && candidate.acceptedTaskId) {
        const taskId = candidate.acceptedTaskId;
        get().deleteTask(taskId);
        get().updateMeeting(meetingId, {
          createdTaskIds: meeting.createdTaskIds.filter((id) => id !== taskId),
        });
      }
      // If replaced, leave the task as-is (title change is permanent).
      acceptedTaskId = undefined;
      newStatus = 'pending';
    } else if (decision.type === 'accept') {
      if (candidate.status === 'accepted') return; // no-op
      const newId = crypto.randomUUID();
      get().addTask({
        id: newId,
        title: candidate.title,
        ownerId: decision.ownerId ?? meeting.hostId,
        dueDate: decision.dueDate,
        completed: false,
        status: 'open',
        delayCount: 0,
        year: meeting.year,
        quarter: meeting.quarter,
      });
      acceptedTaskId = newId;
      get().updateMeeting(meetingId, {
        createdTaskIds: [...meeting.createdTaskIds, newId],
      });
      newStatus = 'accepted';
    } else if (decision.type === 'replace') {
      const matchedId = candidate.matchedTaskId;
      if (!matchedId) return;
      get().updateTask(matchedId, { title: candidate.title });
      acceptedTaskId = matchedId;
      newStatus = 'replaced';
    } else {
      acceptedTaskId = undefined;
      newStatus = 'rejected';
    }

    const updatedSummaries = meeting.fathomSummaries.map((s) => ({
      ...s,
      candidateTasks: s.candidateTasks.map((c) =>
        c.id === candidateId
          ? {
              ...c,
              status: newStatus,
              decidedByUserId: newStatus === 'pending' ? undefined : user?.id,
              decidedAt: newStatus === 'pending' ? undefined : decidedAt,
              acceptedTaskId,
            }
          : c
      ),
    }));
    set((s) => ({
      meetings: s.meetings.map((m) =>
        m.id === meetingId ? { ...m, fathomSummaries: updatedSummaries } : m
      ),
    }));
    // Re-fetch latest meeting (createdTaskIds may have been updated above)
    const latest = get().meetings.find((m) => m.id === meetingId);
    await coreApi(`/records/meetings/${meetingId}`, {
      fathom_summaries: serializeFathomSummaries(latest?.fathomSummaries ?? updatedSummaries),
    }, 'PATCH');
  },

  tryMatchFathomInboxForMeeting: async (meetingId) => {
    const { workspaceId } = get();
    const meeting = get().meetings.find((m) => m.id === meetingId);
    if (!workspaceId || !meeting) return false;
    if (meeting.fathomSummaries.length >= meeting.sessionCount) return false;

    const meetingTypes = useWorkspaceStore.getState().meetingTypes;
    const typeConfig = meetingTypes.find((t) => t.key === (meeting.meetingType ?? 'weekly'));
    const canonicalTitle = (typeConfig?.fathomMeetingTitle ?? '').toString().trim().toLowerCase();

    const meetingDate = new Date(meeting.date + 'T00:00:00');
    const fromIso = new Date(meetingDate.getTime() - 36 * 3600 * 1000).toISOString();
    const toIso = new Date(meetingDate.getTime() + 36 * 3600 * 1000).toISOString();

    const { data: rows } = await supabase
      .from('fathom_webhook_inbox' as any)
      .select('id, payload, fathom_share_url, recorded_at, meeting_title')
      .eq('workspace_id', workspaceId)
      .is('attached_meeting_id', null)
      .gte('recorded_at', fromIso)
      .lte('recorded_at', toIso);

    if (!rows || rows.length === 0) return false;

    const eligible = (rows as any[]).filter((r) => {
      if (!canonicalTitle) return true;
      const t = (r.meeting_title ?? '').toString().trim().toLowerCase();
      return !t || t === canonicalTitle;
    });
    if (eligible.length === 0) return false;

    const sorted = [...eligible].sort((a, b) => {
      const da = Math.abs(new Date(a.recorded_at).getTime() - meetingDate.getTime());
      const db = Math.abs(new Date(b.recorded_at).getTime() - meetingDate.getTime());
      return da - db;
    });
    const pick = sorted[0];
    const payload = pick.payload ?? {};

    const actionItemToTitle = (x: any): string => {
      if (x == null) return '';
      if (typeof x === 'string') return x.trim();
      if (typeof x === 'object') {
        const v = x.description ?? x.title ?? x.text ?? x.name ?? x.action ?? x.task;
        if (typeof v === 'string') return v.trim();
      }
      return '';
    };
    const extractItems = (p: any): { title: string; assigneeName?: string; assigneeEmail?: string }[] => {
      const arr = Array.isArray(p?.action_items) ? p.action_items : Array.isArray(p?.tasks) ? p.tasks : [];
      if (arr.length) {
        return arr.map((x: any) => {
          const title = actionItemToTitle(x);
          if (!title) return null;
          let assigneeName: string | undefined;
          let assigneeEmail: string | undefined;
          if (x && typeof x === 'object' && x.assignee && typeof x.assignee === 'object') {
            if (typeof x.assignee.name === 'string' && x.assignee.name.trim()) assigneeName = x.assignee.name.trim();
            if (typeof x.assignee.email === 'string' && x.assignee.email.trim()) assigneeEmail = x.assignee.email.trim();
          }
          return { title, assigneeName, assigneeEmail };
        }).filter(Boolean) as any[];
      }
      const md: string | undefined = p?.summary ?? p?.markdown;
      if (typeof md === 'string') {
        const m = md.match(/##\s*Action\s*Items[\s\S]*?(?=\n##\s|\n*$)/i);
        if (m) {
          return m[0]
            .split('\n')
            .map((l: string) => l.replace(/^[\s\-*\d.)]+/, '').trim())
            .filter((l: string) => l && !/^##/i.test(l))
            .map((title: string) => ({ title }));
        }
      }
      return [];
    };

    const summaryText: string | undefined = payload.summary ?? payload.markdown ?? payload.transcript_summary;
    const url: string = pick.fathom_share_url ?? payload.share_url ?? payload.url ?? '';
    const { data: { user } } = await supabase.auth.getUser();
    const newSummary: import('@/modules/meetings/types').FathomSummary = {
      url,
      summary: summaryText,
      candidateTasks: extractItems(payload).map((it) => ({
        id: `cand-${crypto.randomUUID().replace(/-/g, '').slice(0, 10)}`,
        title: it.title,
        status: 'pending' as const,
        assigneeName: it.assigneeName,
        assigneeEmail: it.assigneeEmail,
      })),
      attachedAt: new Date().toISOString(),
      attachedByUserId: user?.id,
      source: 'webhook',
    };

    const updatedSummaries = [...meeting.fathomSummaries, newSummary];
    set((s) => ({
      meetings: s.meetings.map((m) =>
        m.id === meetingId ? { ...m, fathomSummaries: updatedSummaries } : m,
      ),
    }));
    await coreApi(`/records/meetings/${meetingId}`, { fathom_summaries: serializeFathomSummaries(updatedSummaries) }, 'PATCH');
    await coreApi(`/records/fathom-recordings/${pick.id}`, { attached_meeting_id: meetingId, match_method: 'auto-on-end' }, 'PATCH');
    return true;
  },

  generateMeetingRecap: async (meetingId) => {
    if (get().generatingRecapIds.includes(meetingId)) return;
    const { meetings: weeklyMeetings, people, issues, tasks } = get();
    const meeting = weeklyMeetings.find((m) => m.id === meetingId);
    if (!meeting || meeting.status === 'in-progress') return;

    const ws = useWorkspaceStore.getState();
    const workspaceName = ws.currentWorkspace?.name ?? 'Your Team';

    const findName = (memberId: string | undefined): string | undefined => {
      if (!memberId) return undefined;
      return people.find((m) => m.id === memberId)?.name;
    };

    const meetingDate = new Date(meeting.date + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });

    // Resolve meeting type label from workspace settings (fall back to raw key)
    const meetingTypeLabel = meeting.meetingType ?? 'weekly';
    const { term } = storeTerms();

    const payload = {
      workspaceId: ws.currentWorkspaceId,
      meetingId,
      workspaceName,
      meetingDate,
      meetingType: meetingTypeLabel,
      hostName: findName(meeting.hostId) ?? 'Unknown',
      attendeeNames: meeting.attendees.map((a) => findName(a.memberId)).filter(Boolean) as string[],
      wins: meeting.attendees
        .filter((a) => a.personalWin || a.professionalWin)
        .map((a) => ({
          member: findName(a.memberId) ?? term('person'),
          personal: a.personalWin,
          professional: a.professionalWin,
        })),
      resolvedIssues: meeting.resolvedIssueIds
        .map((id) => {
          const issue = issues.find((i) => i.id === id);
          if (!issue) return null;
          const ids = meeting.issueNotes?.[id];
          return { title: issue.title, ids };
        })
        .filter(Boolean) as { title: string; ids?: any }[],
      unresolvedIssues: (meeting.unresolvedIssueIds ?? [])
        .map((id) => {
          const issue = issues.find((i) => i.id === id);
          if (!issue) return null;
          return { title: issue.title, owner: findName(issue.ownerId) };
        })
        .filter(Boolean) as { title: string; owner?: string }[],
      createdTasks: meeting.createdTaskIds
        .map((id) => {
          const task = tasks.find((a) => a.id === id);
          if (!task) return null;
          return { title: task.title, owner: findName(task.ownerId), dueDate: task.dueDate };
        })
        .filter(Boolean) as { title: string; owner?: string; dueDate?: string }[],
      rockSnapshot: (meeting.rockSnapshot ?? []).map((s) => ({
        title: s.title,
        status: s.status,
        owner: findName(s.ownerId),
      })),
      fathomSummary: meeting.fathomSummaries.map((s) => s.summary).filter(Boolean).join('\n\n---\n\n') || undefined,
      meetingNotes: meeting.notes,
    };

    set((s) => ({ generatingRecapIds: [...s.generatingRecapIds, meetingId] }));
    try {
      const { data, error } = await supabase.functions.invoke('generate-meeting-recap', { body: payload });
      if (error) throw error;
      const recap: string = data?.recap ?? '';
      set((s) => ({
        meetings: s.meetings.map((m) => m.id === meetingId ? { ...m, meetingRecap: recap } : m),
      }));
      await coreApi(`/records/meetings/${meetingId}`, { meeting_recap: recap }, 'PATCH');
    } finally {
      set((s) => ({ generatingRecapIds: s.generatingRecapIds.filter((id) => id !== meetingId) }));
    }
  },

  saveMeetingRecap: async (meetingId, recap) => {
    set((s) => ({
      meetings: s.meetings.map((m) => m.id === meetingId ? { ...m, meetingRecap: recap } : m),
    }));
    await coreApi(`/records/meetings/${meetingId}`, { meeting_recap: recap }, 'PATCH');
  },

  sendMeetingRecap: async (meetingId, recap, recipients) => {
    const { workspaceId, meetings: weeklyMeetings } = get();
    const meeting = weeklyMeetings.find((m) => m.id === meetingId);
    if (!meeting || !workspaceId) return { sent: 0, failed: recipients.length };

    const ws = useWorkspaceStore.getState();
    const workspaceName = ws.currentWorkspace?.name ?? 'Your Team';
    const { data: { user } } = await supabase.auth.getUser();
    const currentMember = ws.members.find((m) => m.userId === user?.id);
    const senderPerson = get().people.find((t) => t.id === currentMember?.personId);
    const senderName = senderPerson?.name?.split(' ')[0] ?? currentMember?.email?.split('@')[0] ?? user?.email?.split('@')[0] ?? 'A user';

    const meetingDate = new Date(meeting.date + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric',
    });

    const results = await Promise.allSettled(
      recipients.map((r) =>
        supabase.functions.invoke('send-transactional-email', {
          body: {
            templateName: 'meeting-recap',
            recipientEmail: r.email,
            idempotencyKey: `recap-${meetingId}-${r.email}-${Date.now()}`,
            templateData: { recipientName: r.name, senderName, workspaceName, meetingDate, recapMarkdown: recap },
          },
        })
      )
    );

    return {
      sent: results.filter((r) => r.status === 'fulfilled').length,
      failed: results.filter((r) => r.status === 'rejected').length,
    };
  },

  // Planning Checklists
  addPlanningChecklist: async (checklist) => {
    const { workspaceId } = get();
    if (!workspaceId) return;

    const { error } = await ensurePlanningChecklists(
      workspaceId, checklist.cycleType, checklist.year, checklist.quarter ?? null, checklist.items,
    );
    if (error) {
      console.error('[addPlanningChecklist] failed:', error.message);
      return;
    }

    const here = newEntityTeamId(get()) ?? null;
    let q = supabase.from('planning_checklists').select('*')
      .eq('workspace_id', workspaceId)
      .eq('cycle_type', checklist.cycleType);
    q = checklist.year == null ? q.is('year', null) : q.eq('year', checklist.year);
    q = checklist.quarter == null ? q.is('quarter', null) : q.eq('quarter', checklist.quarter);
    if (here) q = q.eq('team_id', here);
    const { data } = await q.maybeSingle();
    if (!data) return;

    const row = planningChecklistFromRow(data);
    set((s) => ({
      planningChecklists: [...s.planningChecklists.filter((c) => c.id !== row.id), row],
    }));
  },
  updatePlanningChecklist: (id, items) => {
    set((s) => ({
      planningChecklists: s.planningChecklists.map((c) =>
        c.id === id ? { ...c, items } : c
      ),
    }));
    applyWrite(coreApi(`/records/planning-checklists/${id}`, { items }, 'PATCH'),
      { noun: 'this checklist', context: 'updatePlanningChecklist', refetch: refetchDomain(get) });
  },

  // Vision
  /**
   * The vision in force for a cycle, IN A SPACE (issue #150).
   *
   * `teamId` is required rather than defaulted to `currentTeamId`. Some call
   * sites are URL-scoped (the Vision page, home) and some are entity-scoped (the
   * meeting wizard reads its MEETING's team, not the URL's) — and D2 established
   * that guessing between those is how a surface silently reads the wrong scope.
   * Passing the root explicitly is a legitimate answer; leaving it implicit is
   * not.
   */
  resolveVision: (year, quarter, teamId) => {
    const { cycles, cycleVisions, visions, workspaceId } = get();
    const teams = useTeamsStore.getState().teams;
    const cyclicalPlanning = useWorkspaceStore.getState().settings?.cyclicalPlanning ?? true;
    const resolved = resolveVisionPure(
      decisions(cycles, cycleVisions), visions, year, quarter, cyclicalPlanning,
      ancestorChain(teams, teamId ?? get().rootTeamId),
    );
    // Last-resort guard, not the mechanism — every real workspace has at least
    // one vision (bootstrap seeds one, and it's always the first revision
    // point). This only fires for a workspace with no visions at all.
    return resolved ?? scratchVision(workspaceId ?? '');
  },

  visionSpan: (visionId) => {
    const { cycles, cycleVisions } = get();
    return visionSpanPure(decisions(cycles, cycleVisions), visionId);
  },

  /**
   * Is this document the one in force right now — for the team that OWNS it?
   *
   * Its own team, not the viewer's (§ 5.2). "Open" governs whether revising
   * edits in place or forks a new document, which is a question about the
   * document's own lineage; resolving it against wherever the reader happens to
   * be standing would let Marketing revising collide with Acme revising.
   */
  isOpenVision: (visionId) => {
    const { cycles, cycleVisions, visions, activeYear, activeQuarter, rootTeamId } = get();
    const teams = useTeamsStore.getState().teams;
    const cyclicalPlanning = useWorkspaceStore.getState().settings?.cyclicalPlanning ?? true;
    const owner = visions.find((v) => v.id === visionId)?.teamId ?? rootTeamId;
    return isOpenVisionPure(
      decisions(cycles, cycleVisions), visions, visionId, activeYear, activeQuarter,
      cyclicalPlanning, ancestorChain(teams, owner),
    );
  },

  listVisions: () => [...get().visions].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),

  createVision: async (name, teamId) => {
    const { workspaceId, visions } = get();
    if (!workspaceId) throw new Error('No workspace');
    const id = crypto.randomUUID();
    const structure = useWorkspaceStore.getState().settings?.visionTemplate ?? null;
    const now = new Date().toISOString();
    const label = name?.trim() || `Untitled vision ${visions.length + 1}`;
    // Caller-supplied teamId takes precedence; otherwise the team you're on.
    const homeTeamId = teamId ?? newEntityTeamId(get());
    const newVision: Vision = { id, workspaceId, teamId: homeTeamId, name: label, structure, data: {}, createdAt: now, updatedAt: now };
    set((s) => ({ visions: [...s.visions, newVision] }));
    const { error } = await coreApi('/records/visions', {
      id, workspace_id: workspaceId, ...(homeTeamId ? { team_id: homeTeamId } : {}), name: label, structure, data: {},
    });
    if (error) {
      console.error('[createVision] failed:', error.message);
      set((s) => ({ visions: s.visions.filter((v) => v.id !== id) }));
      throw error;
    }
    return newVision;
  },

  renameVision: async (id, name) => {
    const { visions } = get();
    const existing = visions.find((v) => v.id === id);
    const trimmed = name.trim();
    if (!existing || !trimmed || trimmed === existing.name) return;
    set((s) => ({ visions: s.visions.map((v) => (v.id === id ? { ...v, name: trimmed } : v)) }));
    const { error } = await coreApi(`/records/visions/${id}`, { name: trimmed }, 'PATCH');
    if (error) console.error('[renameVision] failed:', error.message);
  },

  cloneVision: async (id, targetTeamId) => {
    const { visions, workspaceId } = get();
    const src = visions.find((v) => v.id === id);
    if (!src || !workspaceId) throw new Error('Vision not found');
    const newId = crypto.randomUUID();
    const now = new Date().toISOString();
    const label = `${src.name} (copy)`;
    // Caller may specify the target team (e.g. when cloning an ancestor's vision
    // onto the current team). Otherwise lands where the reader is standing.
    const cloneTeamId = targetTeamId ?? newEntityTeamId(get());
    const cloned: Vision = { id: newId, workspaceId, teamId: cloneTeamId, name: label, structure: src.structure, data: src.data, createdAt: now, updatedAt: now };
    set((s) => ({ visions: [...s.visions, cloned] }));
    const { error } = await coreApi('/records/visions', {
      id: newId, workspace_id: workspaceId, team_id: cloneTeamId ?? get().rootTeamId, name: label,
      structure: src.structure ?? null, data: src.data, created_as: 'cloned',
    });
    if (error) {
      console.error('[cloneVision] failed:', error.message);
      set((s) => ({ visions: s.visions.filter((v) => v.id !== newId) }));
      throw error;
    }
    return cloned;
  },

  deleteVision: async (id) => {
    const { visions } = get();
    const existing = visions.find((v) => v.id === id);
    if (!existing) return;
    // The DB cascades cycle_visions rows away with the vision; mirror that
    // locally rather than leaving a null-vision row, which would read as a
    // deliberate inherit tombstone.
    set((s) => ({
      visions: s.visions.filter((v) => v.id !== id),
      cycleVisions: s.cycleVisions.filter((cv) => cv.visionId !== id),
    }));
    const { error } = await coreApi(`/records/visions/${id}`, {}, 'DELETE');
    if (error) console.error('[deleteVision] failed:', error.message);
  },

  saveVisionInPlace: async (id, patch) => {
    const { visions } = get();
    const existing = visions.find((v) => v.id === id);
    if (!existing) return;
    const now = new Date().toISOString();
    set((s) => ({
      visions: s.visions.map((v) => (v.id === id ? { ...v, ...patch, updatedAt: now } : v)),
    }));
    const { error } = await coreApi(`/records/visions/${id}`, { ...visionToRow(patch), updated_at: now }, 'PATCH');
    if (error) console.error('[saveVisionInPlace] failed:', error.message);
  },

  ratifyVision: async (id) => {
    const { visions } = get();
    const existing = visions.find((v) => v.id === id);
    if (!existing) return;
    const now = new Date().toISOString();
    set((s) => ({ visions: s.visions.map((v) => (v.id === id ? { ...v, ratifiedAt: now, updatedAt: now } : v)) }));
    const { error } = await coreApi(`/records/visions/${id}`, { ratified_at: now }, 'PATCH');
    if (error) console.error('[ratifyVision] failed:', error.message);
  },

  saveVisionAsNew: async (fromId, name, patch, attachAt) => {
    const { visions, workspaceId } = get();
    const src = visions.find((v) => v.id === fromId);
    if (!workspaceId) throw new Error('No workspace');
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    const label = name.trim() || `${src?.name ?? 'Vision'} (new)`;
    const structure = patch.structure ?? src?.structure ?? null;
    const data = patch.data ?? src?.data ?? {};
    // The source's team unless the caller names another (§ 5.2). Marketing
    // revising Acme's inherited vision produces a MARKETING document only if
    // something asks for that explicitly; otherwise the revision belongs to the
    // document's own lineage and Acme stays its owner.
    const homeTeamId = attachAt?.teamId ?? src?.teamId ?? get().rootTeamId;
    const newVision: Vision = { id, workspaceId, teamId: homeTeamId ?? undefined, name: label, structure, data, createdAt: now, updatedAt: now };
    set((s) => ({ visions: [...s.visions, newVision] }));
    const { error } = await coreApi('/records/visions', {
      id, workspace_id: workspaceId, ...(homeTeamId ? { team_id: homeTeamId } : {}), name: label,
      structure: structure ?? null, data,
    });
    if (error) {
      console.error('[saveVisionAsNew] failed:', error.message);
      set((s) => ({ visions: s.visions.filter((v) => v.id !== id) }));
      throw error;
    }
    if (attachAt) await get().setVisionDecision(attachAt.year, attachAt.quarter, id, homeTeamId);
    return newVision;
  },

  updateVisionTemplate: async (id, structure) => {
    const { visions } = get();
    const existing = visions.find((v) => v.id === id);
    if (!existing) return;
    set((s) => ({ visions: s.visions.map((v) => (v.id === id ? { ...v, structure } : v)) }));
    const { error } = await coreApi(`/records/visions/${id}`, { structure }, 'PATCH');
    if (error) console.error('[updateVisionTemplate] failed:', error.message);
  },

  ensureRootVisionDecision: async (year, quarter, visionId) => {
    const { cycles, cycleVisions, visions, rootTeamId } = get();
    const chain = ancestorChain(useTeamsStore.getState().teams, rootTeamId);
    if (resolveVisionPure(decisions(cycles, cycleVisions), visions, year, quarter, true, chain)) return;
    await get().setVisionDecision(year, quarter, visionId, rootTeamId);
  },

  setVisionDecision: async (year, quarter, visionId, teamId) => {
    const { workspaceId, cycles, rootTeamId } = get();
    if (!workspaceId) return;
    const cycle = cycles.find((c) =>
      c.workspaceId === workspaceId && c.year === year && (c.quarter ?? null) === (quarter ?? null));
    if (!rootTeamId || !cycle) {
      console.error('[setVisionDecision] skipped:', !rootTeamId ? 'no root team loaded' : `no cycle row for ${cycleLabel(year, quarter)}`);
      storeNotice.error(`Couldn't set the vision for ${cycleLabel(year, quarter)}`);
      return;
    }

    // THE SPACE THE DECISION BELONGS TO (#150). A decision says "in team S,
    // from cycle C, the vision is V" — `cycle_visions` is keyed `(cycle_id,
    // team_id)` precisely so each team has its own timeline.
    //
    // Derived from the document rather than from the page: setting a decision for
    // Marketing's vision belongs in Marketing whichever screen you did it from.
    // Clearing and the inherit tombstone have no document to ask, so they fall
    // back to the caller-supplied teamId, then the current page's team.
    const isInherit = visionId === 'inherit';
    const pointTeamId = teamId ?? (!isInherit && visionId
      ? get().visions.find((v) => v.id === visionId)?.teamId
      : undefined) ?? newEntityTeamId(get()) ?? rootTeamId;

    if (visionId == null) {
      set((s) => ({
        cycleVisions: s.cycleVisions.filter(
          (cv) => !(cv.cycleId === cycle.id && cv.teamId === pointTeamId)),
      }));
      const { error } = await coreApi('/vision-decisions', { cycle_id: cycle.id, team_id: pointTeamId, clear: true });
      if (error) console.error('[setVisionDecision] clear failed:', error.message);
      return;
    }

    // 'inherit' writes a null vision_id tombstone — a deliberate choice to
    // inherit from the parent team, distinct from the absence of a row.
    const resolvedVisionId: string | null = isInherit ? null : visionId as string;

    if (!isInherit) {
      const visionHomeTeamId = get().visions.find((v) => v.id === resolvedVisionId)?.teamId;
      if (visionHomeTeamId && visionHomeTeamId !== pointTeamId) {
        console.error('[setVisionDecision] team mismatch: vision is homed in', visionHomeTeamId, 'but target team is', pointTeamId);
        return;
      }
    }

    const existing = get().cycleVisions.find(
      (cv) => cv.cycleId === cycle.id && cv.teamId === pointTeamId);
    const id = existing?.id ?? crypto.randomUUID();
    set((s) => ({
      cycleVisions: existing
        ? s.cycleVisions.map((cv) => (cv.id === existing.id ? { ...cv, visionId: resolvedVisionId } : cv))
        : [...s.cycleVisions, { id, cycleId: cycle.id, teamId: pointTeamId, visionId: resolvedVisionId }],
    }));
    const { error } = await coreApi('/vision-decisions', {
      id, cycle_id: cycle.id, team_id: pointTeamId, vision_id: resolvedVisionId,
    });
    if (error) console.error('[setVisionDecision] failed:', error.message);
  },

  moveVisionDecision: async (visionId, target) => {
    const { cycles, cycleVisions, visions } = get();
    const point = cycleVisions.find((cv) => cv.visionId === visionId);
    const current = point ? cycles.find((c) => c.id === point.cycleId) : undefined;
    const visionTeamId = visions.find((v) => v.id === visionId)?.teamId;
    if (current && (current.year !== target.year || (current.quarter ?? null) !== (target.quarter ?? null))) {
      await get().setVisionDecision(current.year, current.quarter ?? null, null, visionTeamId);
    }
    await get().setVisionDecision(target.year, target.quarter, visionId);
  },

  stopInheriting: async (year, quarter, teamId) => {
    const { workspaceId } = get();
    if (!workspaceId) return null;
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    const structure = useWorkspaceStore.getState().settings?.visionTemplate ?? null;
    const newVision: Vision = { id, workspaceId, teamId, name: 'New vision', structure, data: {}, createdAt: now, updatedAt: now };
    set((s) => ({ visions: [...s.visions, newVision] }));
    const { error } = await coreApi('/records/visions', {
      id, workspace_id: workspaceId, team_id: teamId, name: 'New vision', structure, data: {},
      created_as: 'vision-decision-set',
    });
    if (error) {
      console.error('[stopInheriting] failed to create vision:', error.message);
      set((s) => ({ visions: s.visions.filter((v) => v.id !== id) }));
      return null;
    }
    await get().setVisionDecision(year, quarter, id, teamId);
    return newVision;
  },

  detachVision: async (visionId) => {
    const { cycleVisions, cycles } = get();
    const cv = cycleVisions.find((c) => c.visionId === visionId);
    if (!cv) return [];
    const cycle = cycles.find((c) => c.id === cv.cycleId);
    if (!cycle) return [];
    const affected = [{ year: cycle.year, quarter: cycle.quarter ?? null }];
    await get().setVisionDecision(cycle.year, cycle.quarter ?? null, null, cv.teamId);
    return affected;
  },

  /** The only automatic vision write — see branchVisionForYear below. */
  branchVisionForYear: async (year, quarter) => {
    const { cycles, cycleVisions, visions, workspaceId, rootTeamId } = get();
    const cyclicalPlanning = useWorkspaceStore.getState().settings?.cyclicalPlanning ?? true;
    if (!workspaceId || !cyclicalPlanning) return;
    const points = decisions(cycles, cycleVisions);
    // The ROOT's chain, and only the root's. This is the one automatic vision
    // write, fired when a new year opens, and it branches the document the
    // tenant as a whole works to.
    //
    // KNOWN LIMIT (#150): a team that has forked its own vision does not get an
    // automatic branch — its document simply carries into the new year until
    // someone revises it. Branching every team's fork would multiply the one
    // automatic write by the size of the tree, and deciding whether that is
    // wanted is a §5.2 divergence question, which this issue is explicitly not.
    const chain = ancestorChain(useTeamsStore.getState().teams, rootTeamId);
    const current = resolveVisionPure(points, visions, year, quarter, true, chain)
      ?? resolveVisionPure(points, visions, year, quarter, false, chain);
    if (!current) return;
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    const label = String(year);
    const branchedData = clearOneYearKeys(current.data ?? {});
    const branched: Vision = { id, workspaceId, teamId: current.teamId, name: label, structure: current.structure, data: branchedData, createdAt: now, updatedAt: now };
    set((s) => ({ visions: [...s.visions, branched] }));
    const { error } = await coreApi('/records/visions', {
      id, workspace_id: workspaceId, team_id: current.teamId ?? get().rootTeamId, name: label,
      structure: current.structure ?? null, data: branchedData, created_as: 'vision-decision-set',
    });
    if (error) {
      console.error('[branchVisionForYear] failed:', error.message);
      set((s) => ({ visions: s.visions.filter((v) => v.id !== id) }));
      return;
    }
    await get().setVisionDecision(year, quarter, id);
  },

  // ─── Ideas (unified parking lot) ──────────────────────────
  addIdea: async (idea) => {
    const { workspaceId } = get();
    const id = idea.id ?? crypto.randomUUID();
    const { data: { user: authUser } } = await supabase.auth.getUser();
    const newIdea: Idea = {
      id,
      workspaceId: workspaceId ?? undefined,
      teamId: idea.teamId ?? newEntityTeamId(get()),
      title: idea.title,
      description: idea.description,
      status: idea.status ?? 'open',
      targetQuarter: idea.targetQuarter,
      ownerId: idea.ownerId,
      goalId: idea.goalId,
      milestones: idea.milestones ?? [],
      linkedMetricIds: idea.linkedMetricIds ?? [],
      promotedRockId: idea.promotedRockId,
      planningSessionId: idea.planningSessionId,
      sourceRockId: idea.sourceRockId,
      sourceGoalId: idea.sourceGoalId,
      sourceOwnerId: idea.sourceOwnerId,
      sourceMilestones: idea.sourceMilestones ?? [],
      combinedIntoId: idea.combinedIntoId,
      sourceIssueId: idea.sourceIssueId,
      createdAt: new Date().toISOString(),
      createdBy: idea.createdBy ?? authUser?.id,
    };
    set((s) => ({ ideas: [...s.ideas, newIdea] }));
    await coreApi('/records/ideas', {
      ...(newIdea.teamId ? { team_id: newIdea.teamId } : {}),
      id, workspace_id: workspaceId,
      title: newIdea.title, description: newIdea.description ?? null,
      status: newIdea.status,
      target_quarter: newIdea.targetQuarter ?? null,
      owner_id: newIdea.ownerId ?? null,
      goal_id: newIdea.goalId ?? null,
      milestones: (newIdea.milestones ?? []) as any,
      linked_metric_ids: (newIdea.linkedMetricIds ?? []) as any,
      promoted_rock_id: newIdea.promotedRockId ?? null,
      planning_session_id: newIdea.planningSessionId ?? null,
      source_rock_id: newIdea.sourceRockId ?? null,
      source_goal_id: newIdea.sourceGoalId ?? null,
      source_owner_id: newIdea.sourceOwnerId ?? null,
      source_milestones: (newIdea.sourceMilestones ?? []) as any,
      combined_into_id: newIdea.combinedIntoId ?? null,
      source_issue_id: newIdea.sourceIssueId ?? null,
      created_by: newIdea.createdBy ?? null,
    });
    return newIdea;
  },
  updateIdea: async (id, updates) => {
    set((s) => ({ ideas: s.ideas.map((i) => i.id === id ? { ...i, ...updates } : i) }));
    const dbUpdates: any = {};
    if (updates.title !== undefined) dbUpdates.title = updates.title;
    if (updates.description !== undefined) dbUpdates.description = updates.description ?? null;
    if (updates.status !== undefined) dbUpdates.status = updates.status;
    if (updates.targetQuarter !== undefined) dbUpdates.target_quarter = updates.targetQuarter ?? null;
    if (updates.ownerId !== undefined) dbUpdates.owner_id = updates.ownerId ?? null;
    if (updates.goalId !== undefined) dbUpdates.goal_id = updates.goalId ?? null;
    if (updates.milestones !== undefined) dbUpdates.milestones = updates.milestones as any;
    if (updates.linkedMetricIds !== undefined) dbUpdates.linked_metric_ids = updates.linkedMetricIds as any;
    if (updates.promotedRockId !== undefined) dbUpdates.promoted_rock_id = updates.promotedRockId ?? null;
    if (updates.planningSessionId !== undefined) dbUpdates.planning_session_id = updates.planningSessionId ?? null;
    if (updates.combinedIntoId !== undefined) dbUpdates.combined_into_id = updates.combinedIntoId ?? null;
    if (updates.sourceIssueId !== undefined) dbUpdates.source_issue_id = updates.sourceIssueId ?? null;
    await coreApi(`/records/ideas/${id}`, dbUpdates, 'PATCH');
  },
  deleteIdea: async (id) => {
    set((s) => ({ ideas: s.ideas.filter((i) => i.id !== id) }));
    await coreApi(`/records/ideas/${id}`, {}, 'DELETE');
  },
  combineIdeas: async (sourceIds, targetId) => {
    for (const sid of sourceIds) {
      if (sid === targetId) continue;
      await get().updateIdea(sid, { status: 'combined', combinedIntoId: targetId });
    }
  },
  promoteIdeaToRock: async (ideaId, sessionId, rockPatch) => {
    const idea = get().ideas.find((i) => i.id === ideaId);
    if (!idea) throw new Error('Idea not found');
    const rockId = rockPatch.id ?? crypto.randomUUID();
    const rock: Rock = {
      id: rockId,
      goalId: rockPatch.goalId ?? idea.goalId ?? idea.sourceGoalId,
      title: rockPatch.title,
      description: rockPatch.description ?? idea.description,
      status: 'on-track',
      ownerId: rockPatch.ownerId ?? idea.ownerId ?? idea.sourceOwnerId,
      quarter: rockPatch.quarter,
      year: rockPatch.year,
      dueDate: rockPatch.dueDate,
      // The idea's team, not the viewer's: a meeting can be concluded from
      // anywhere, including a team it does not belong to.
      teamId: rockPatch.teamId ?? idea.teamId,
      // Direct lineage, so a carried-forward rock stays reachable from its
      // predecessor once the idea that carried it is archived or combined.
      sourceRockId: idea.sourceRockId,
    };
    get().addRock(rock);

    // Milestones, in order of preference.
    //
    // A rock carried forward from a live predecessor brings that predecessor's
    // unfinished plan with it — dates, owners, sizes and the dependencies
    // between them — re-based so the earliest surviving milestone starts with
    // the new quarter (#251). Completed ones stay behind: they happened, and
    // they happened last quarter.
    //
    // Baselines do not travel. Each carried milestone is baselined at its
    // re-based dates, because that is what the team is agreeing to now; the old
    // baseline stays with the closed cycle as its record.
    const carried = idea.sourceRockId
      ? get().milestones
          .filter((m) => m.rockId === idea.sourceRockId && !m.completed)
          .sort((a, b) => a.position - b.position)
      : [];

    const ideaMilestones = (idea.milestones ?? []).length > 0 ? idea.milestones! : null;
    const sourceMilestones = idea.sourceMilestones ?? [];

    let milestonesToCreate: Milestone[];
    /** Old milestone id → the copy that replaces it, so the edges can follow. */
    const carriedIds = new Map<string, string>();

    if (carried.length) {
      const anchor = carried
        .map((m) => m.startDate ?? m.endDate)
        .filter((d): d is string => !!d)
        .sort()[0];
      const quarterStart = getQuarterStartDateStr(rockPatch.year, rockPatch.quarter);
      const shiftDays = anchor
        ? differenceInCalendarDays(parseDateStr(quarterStart), parseDateStr(anchor))
        : 0;
      const rebase = (d?: string) => (d && shiftDays ? shiftDateStr(d, shiftDays) : d);

      milestonesToCreate = carried.map((m, i) => {
        const id = crypto.randomUUID();
        carriedIds.set(m.id, id);
        return {
          id,
          rockId,
          title: m.title,
          completed: false,
          ownerId: m.ownerId,
          startDate: rebase(m.startDate),
          endDate: rebase(m.endDate),
          weight: m.weight,
          position: i,
          sourceMilestoneId: m.id,
        };
      });
    } else if (ideaMilestones) {
      milestonesToCreate = ideaMilestones.map((m, i) => ({
        id: crypto.randomUUID(), rockId, title: m.title, completed: false,
        endDate: m.endDate, position: i,
        sourceMilestoneId: undefined as string | undefined,
      }));
    } else {
      milestonesToCreate = sourceMilestones.map((m, i) => ({
        id: crypto.randomUUID(), rockId, title: m.title, completed: false,
        endDate: undefined as string | undefined, position: i,
        sourceMilestoneId: m.id,
      }));
    }

    for (const m of milestonesToCreate) {
      await get().addMilestone(m);
    }

    // The sequencing survives the quarter boundary. An edge whose other end was
    // already finished has nothing left to wait for, so it is not recreated.
    if (carriedIds.size) {
      const edges = get().milestoneDependencies.filter(
        (d) => carriedIds.has(d.predecessorId) && carriedIds.has(d.successorId),
      );
      for (const edge of edges) {
        await get().addMilestoneDependency(
          carriedIds.get(edge.predecessorId)!, carriedIds.get(edge.successorId)!,
          { lagDays: edge.lagDays, type: edge.type },
        );
      }
    }

    // Copy update history when carrying forward from a prior quarter
    if (idea.sourceRockId) {
      const { workspaceId } = get();
      const sourceRock = get().rocks.find((r) => r.id === idea.sourceRockId);
      const sourceUpdates = get().rockUpdates.filter((u) => u.rockId === idea.sourceRockId);
      if (workspaceId && sourceUpdates.length > 0) {
        const copied = sourceUpdates.map((u) => ({
          ...u,
          id: crypto.randomUUID(),
          rockId,
          workspaceId,
          createdAt: u.createdAt,
        }));
        set((s) => ({ rockUpdates: [...s.rockUpdates, ...copied] }));
        await coreApi(`/rocks/${rockId}/updates/copy`, {
          source_rock_id: idea.sourceRockId,
          ids: Object.fromEntries(sourceUpdates.map((u, index) => [u.id, copied[index].id])),
        });
      }
      // System update on new rock: "carried forward from Q(n-1)"
      if (sourceRock) {
        await get().addRockUpdate({
          rockId,
          message: `Carried forward from Q${sourceRock.quarter} ${sourceRock.year}`,
          statusAtUpdate: 'on-track',
          isSystemGenerated: true,
        });
        // System update on old rock: "continued in Q(n+1)" — status unchanged
        await get().addRockUpdate({
          rockId: idea.sourceRockId,
          message: `Continued in Q${rock.quarter} ${rock.year}`,
          statusAtUpdate: sourceRock.status,
          isSystemGenerated: true,
        });
      }
    }

    // Carry-forward stakeholder matrix from source rock if present
    if (idea.sourceRockId) {
      const stakeholderStore = useStakeholderStore.getState();
      const wsId = get().workspaceId;
      if (wsId) {
        const sourceMatrixKey = `rock:${idea.sourceRockId}`;
        let sourceMatrix = stakeholderStore.matrices[sourceMatrixKey];
        if (!sourceMatrix) {
          await stakeholderStore.loadMatrix('rock', idea.sourceRockId);
          sourceMatrix = useStakeholderStore.getState().matrices[sourceMatrixKey] as import('@/modules/stakeholders/types').StakeholderMatrix | undefined;
        }
        if (sourceMatrix && sourceMatrix.members.length > 0) {
          // Build milestoneId remap: old id → new id using source_milestone_id
          const milestoneRemap = new Map<string, string>();
          for (const newMs of milestonesToCreate) {
            if (newMs.sourceMilestoneId) {
              milestoneRemap.set(newMs.sourceMilestoneId, newMs.id);
            }
          }

          // Remap assignments keys
          const remappedAssignments: import('@/modules/stakeholders/types').StakeholderAssignments = {};
          for (const [oldMsId, byMember] of Object.entries(sourceMatrix.assignments)) {
            const newMsId = milestoneRemap.get(oldMsId);
            if (newMsId) remappedAssignments[newMsId] = { ...byMember };
          }

          // If new rock owner differs from source, swap positions
          const newOwnerId = rock.ownerId;
          const sourceOwnerId = get().rocks.find((r) => r.id === idea.sourceRockId)?.ownerId;
          let newMembers = [...sourceMatrix.members];
          if (newOwnerId && newOwnerId !== sourceOwnerId) {
            const existingNewOwner = newMembers.find((m) => m.memberId === newOwnerId);
            const existingSourceOwner = newMembers.find((m) => m.memberId === sourceOwnerId);
            if (!existingNewOwner && newOwnerId) {
              // Shift everyone up and put new owner at 0
              newMembers = [
                { memberId: newOwnerId, position: 0 },
                ...newMembers.map((m, i) => ({ ...m, position: i + 1 })),
              ];
            } else if (existingNewOwner && existingSourceOwner) {
              // Swap positions
              const p0 = existingNewOwner.position;
              const p1 = existingSourceOwner.position;
              newMembers = newMembers.map((m) => {
                if (m.memberId === newOwnerId) return { ...m, position: 0 };
                if (m.memberId === sourceOwnerId) return { ...m, position: p0 };
                return m;
              });
            }
          }

          const newMatrix: import('@/modules/stakeholders/types').StakeholderMatrix = {
            id: crypto.randomUUID(),
            workspaceId: wsId,
            entityType: 'rock',
            entityId: rockId,
            frameworkId: sourceMatrix.frameworkId,
            members: newMembers,
            assignments: remappedAssignments,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          await stakeholderStore.saveMatrix(newMatrix);
        }
      }
    }

    await get().updateIdea(ideaId, { status: 'promoted', promotedRockId: rockId });
    if (sessionId) {
      await get().recordDecision({
        sessionId,
        step: 'promote',
        ideaKind: 'rock',
        ideaId,
        decision: 'promoted',
        promotedTargetId: rockId,
      });
    }
    return rock;
  },

  // ─── Planning sessions ────────────────────────────────────
  ensurePlanningSession: async (args) => {
    const { workspaceId } = get();
    const teamId = args.teamId !== undefined ? args.teamId : (newEntityTeamId(get()) ?? null);
    // At the root the store holds every team's sessions, so cycle alone would
    // match a child team's run.
    const existing = get().planningSessions.find(
      (p) => p.kind === args.kind && (p.year ?? null) === (args.year ?? null)
        && (p.quarter ?? null) === (args.quarter ?? null)
        && (p.teamId ?? null) === teamId,
    );
    if (existing) return existing;

    const id = crypto.randomUUID();
    const session: PlanningSession = {
      id,
      workspaceId: workspaceId ?? undefined,
      teamId: teamId ?? undefined,
      kind: args.kind,
      year: args.year,
      quarter: args.quarter,
      prevYear: args.prevYear,
      prevQuarter: args.prevQuarter,
      startedAt: new Date().toISOString(),
      facilitatorId: args.facilitatorId,
    };
    const { error } = await coreApi('/records/planning-sessions', {
      team_id: teamId,
      id, workspace_id: workspaceId,
      kind: session.kind,
      year: args.year ?? null,
      quarter: args.quarter ?? null,
      prev_year: args.prevYear ?? null,
      prev_quarter: args.prevQuarter ?? null,
      facilitator_id: session.facilitatorId ?? null,
    });

    if (error) {
      // A concurrent host won the unique key, or the row predates this snapshot.
      let q = supabase.from('planning_sessions')
        .select('*')
        .eq('workspace_id', workspaceId!)
        .eq('kind', args.kind);
      q = args.year == null ? q.is('year', null) : q.eq('year', args.year);
      q = args.quarter == null ? q.is('quarter', null) : q.eq('quarter', args.quarter);
      if (teamId) q = q.eq('team_id', teamId);
      const { data: found } = await q.maybeSingle();
      if (!found) throw error;
      const won = planningSessionFromRow(found);
      set((s) => ({ planningSessions: [...s.planningSessions.filter((p) => p.id !== won.id), won] }));
      return won;
    }

    set((s) => ({ planningSessions: [...s.planningSessions, session] }));
    return session;
  },

  recordDecision: async (args) => {
    const { workspaceId } = get();
    const id = crypto.randomUUID();
    const dec: RockDecision = {
      id,
      workspaceId: workspaceId ?? undefined,
      sessionId: args.sessionId,
      step: args.step,
      ideaKind: args.ideaKind,
      ideaId: args.ideaId,
      rockId: args.rockId,
      decision: args.decision,
      combinedIntoId: args.combinedIntoId,
      promotedTargetId: args.promotedTargetId,
      rationale: args.rationale,
      decidedAt: new Date().toISOString(),
      decidedBy: args.decidedBy,
    };
    set((s) => ({ rockDecisions: [...s.rockDecisions, dec] }));
    await coreApi('/records/rock-decisions', {
      id, workspace_id: workspaceId,
      session_id: dec.sessionId,
      step: dec.step,
      idea_kind: dec.ideaKind ?? null,
      idea_id: dec.ideaId ?? null,
      rock_id: dec.rockId ?? null,
      decision: dec.decision,
      combined_into_id: dec.combinedIntoId ?? null,
      promoted_target_id: dec.promotedTargetId ?? null,
      rationale: dec.rationale ?? null,
      decided_by: dec.decidedBy ?? null,
    });
    return dec;
  },

  seedCarryForwardIdea: async ({ sessionId, rock, targetQuarter }) => {
    // Idempotent: skip if a non-terminal idea for this rock already exists.
    const existing = get().ideas.find(
      (i) => i.sourceRockId === rock.id && i.status !== 'archived' && i.status !== 'combined',
    );
    if (existing) return null;
    const sourceMilestones: RockIdeaSourceMilestone[] = get().milestones
      .filter((m) => m.rockId === rock.id && !m.completed)
      .map((m) => ({ id: m.id, title: m.title }));
    const idea = await get().addIdea({
      title: rock.title,
      description: rock.description,
      targetQuarter,
      planningSessionId: sessionId,
      sourceRockId: rock.id,
      sourceGoalId: rock.goalId,
      sourceOwnerId: rock.ownerId,
      sourceMilestones,
      teamId: rock.teamId,
    });
    return idea;
  },

  promoteGoalIdeaToGoal: async (ideaId, sessionId, goalPatch) => {
    const idea = get().ideas.find((g) => g.id === ideaId);
    if (!idea) throw new Error('Idea not found');
    const goalId = goalPatch.id ?? crypto.randomUUID();
    const goal: AnnualGoal = {
      id: goalId,
      title: goalPatch.title,
      description: goalPatch.description ?? idea.description,
      year: goalPatch.year,
    };
    get().addGoal(goal);
    await get().recordDecision({
      sessionId,
      step: 'promote',
      ideaKind: 'goal',
      ideaId,
      decision: 'promoted',
      promotedTargetId: goalId,
    });
    return goal;
  },

  completePlanningSession: async (sessionId, notes) => {
    const completedAt = new Date().toISOString();
    set((s) => ({
      planningSessions: s.planningSessions.map((p) =>
        p.id === sessionId ? { ...p, completedAt, notes: notes ?? p.notes } : p,
      ),
    }));
    const dbUpdates: any = { completed_at: completedAt };
    if (notes !== undefined) dbUpdates.notes = notes ?? null;
    const { error } = await coreApi(`/records/planning-sessions/${sessionId}`, dbUpdates, 'PATCH');
    if (error) {
      // Closing a cycle touches every team's run, including ones this admin
      // cannot write; a rejected row must not read as completed here.
      set((s) => ({
        planningSessions: s.planningSessions.map((p) =>
          p.id === sessionId ? { ...p, completedAt: undefined } : p,
        ),
      }));
    }
  },

  subscribeToRealtime: (workspaceId) => {
    get().unsubscribeFromRealtime();

    // One value, patched into the metric that holds it. Replace the matching
    // period in place so the rest of the metric's values keep their order.
    const applyLiveEntry = (r: any) => {
      const entry: MetricEntry = {
        period: r.period,
        value: r.value === null || r.value === undefined ? null : Number(r.value),
        enteredBy: r.entered_by ?? null,
        enteredAt: r.entered_at ?? null,
      };
      set((s) => ({
        metrics: s.metrics.map((k) => {
          if (k.id !== r.metric_id) return k;
          const has = k.entries.some((e) => e.period === entry.period);
          return {
            ...k,
            entries: has
              ? k.entries.map((e) => e.period === entry.period ? entry : e)
              : [...k.entries, entry].sort((a, b) => a.period.localeCompare(b.period)),
          };
        }),
      }));
    };
    const subscriptionGeneration = _operatingSubscriptionGeneration;

    const setup = () => {
      _operatingChannel = supabase
        .channel(`operating-${workspaceId}-${Date.now()}`)
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'rocks', filter: `workspace_id=eq.${workspaceId}` },
          (payload) => {
            if (subscriptionGeneration !== _operatingSubscriptionGeneration) return;
            const r = payload.new as any;
            const updated: Rock = {
              id: r.id, goalId: r.goal_id ?? undefined, title: r.title,
              description: r.description ?? undefined, status: r.status as any,
              ownerId: r.owner_id ?? undefined, quarter: r.quarter, year: r.year,
              dueDate: r.due_date ?? undefined,
            };
            set((s) => ({
              rocks: s.rocks.map((rock) => rock.id === updated.id ? { ...rock, ...updated } : rock),
            }));
          }
        )
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'metrics', filter: `workspace_id=eq.${workspaceId}` },
          (payload) => {
            if (subscriptionGeneration !== _operatingSubscriptionGeneration) return;
            const r = payload.new as any;
            set((s) => ({
              metrics: s.metrics.map((k) =>
                k.id === r.id
                  // A `metrics` UPDATE carries no values: they live in their
                  // own table and arrive through their own subscription below.
                  ? { ...metricFromRow(r), entries: k.entries }
                  : k
              ),
            }));
          }
        )
        // Values arrive on their own table now. INSERT and UPDATE only, for the
        // reason spelled out below: a '*' subscription would broadcast the old
        // row of a deleted value, which realtime does not filter by RLS.
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'metric_entries', filter: `workspace_id=eq.${workspaceId}` },
          (payload) => {
            if (subscriptionGeneration !== _operatingSubscriptionGeneration) return;
            applyLiveEntry(payload.new as any);
          }
        )
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'metric_entries', filter: `workspace_id=eq.${workspaceId}` },
          (payload) => {
            if (subscriptionGeneration !== _operatingSubscriptionGeneration) return;
            applyLiveEntry(payload.new as any);
          }
        )
        // INSERT and UPDATE only, never '*'. Realtime does not apply RLS to
        // DELETE payloads in postgres_changes, so a '*' subscription would tell
        // every subscriber matching the workspace filter about a deletion it
        // could not have read — including members of no team that owns it.
        //
        // Measured against the current stack that payload is the primary key
        // alone rather than the whole row, which narrows the leak but does not
        // remove it, and is not a guarantee this version pins.
        //
        // The workspace filter is a coarse prefilter, not authorization; the
        // RLS policy is (TEAMS.md § 5.5). That works for INSERT and UPDATE and
        // silently does not for DELETE.
        //
        // A deleted scorecard therefore disappears on the next fetch rather
        // than live. `src/test/integration/realtime-bindings.test.ts` enforces
        // this and that every bound table is published (#395, #396).
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'scorecards', filter: `workspace_id=eq.${workspaceId}` },
          (payload) => {
            if (subscriptionGeneration !== _operatingSubscriptionGeneration) return;
            const sc = scorecardFromRow(payload.new as any);
            set((s) => (s.scorecards.some((x) => x.id === sc.id) ? s : { scorecards: [...s.scorecards, sc] }));
          }
        )
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'scorecards', filter: `workspace_id=eq.${workspaceId}` },
          (payload) => {
            if (subscriptionGeneration !== _operatingSubscriptionGeneration) return;
            const sc = scorecardFromRow(payload.new as any);
            set((s) => ({ scorecards: s.scorecards.map((x) => (x.id === sc.id ? sc : x)) }));
          }
        )
        // Commentary on a rock is what moves during a meeting, so it is worth
        // broadcasting. INSERT and UPDATE only, for the reason above.
        //
        // Both handlers drop an update whose rock is not in local state. The
        // workspace filter is coarser than the loaded set — `rockUpdates` is
        // team-scoped through its rock (`scopedVia`) — so accepting one
        // unconditionally would pull in commentary from a team this client is
        // not viewing. The `rocks` binding is safe for the same reason without
        // saying so: mapping over existing rows already ignores the rest.
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'rock_updates', filter: `workspace_id=eq.${workspaceId}` },
          (payload) => {
            if (subscriptionGeneration !== _operatingSubscriptionGeneration) return;
            const update = rockUpdateFromRow(payload.new as any);
            set((s) => {
              if (!s.rocks.some((r) => r.id === update.rockId)) return s;
              if (s.rockUpdates.some((u) => u.id === update.id)) return s;
              return { rockUpdates: [update, ...s.rockUpdates] };
            });
          }
        )
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'rock_updates', filter: `workspace_id=eq.${workspaceId}` },
          (payload) => {
            if (subscriptionGeneration !== _operatingSubscriptionGeneration) return;
            const update = rockUpdateFromRow(payload.new as any);
            set((s) => (s.rocks.some((r) => r.id === update.rockId)
              ? { rockUpdates: s.rockUpdates.map((u) => (u.id === update.id ? update : u)) }
              : s));
          }
        )
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'tasks', filter: `workspace_id=eq.${workspaceId}` },
          (payload) => {
            if (subscriptionGeneration !== _operatingSubscriptionGeneration) return;
            set((s) => {
              const tasks = withInsertedTask(s.tasks, payload.new as any, newEntityTeamId(s));
              return tasks === s.tasks ? s : { tasks };
            });
          }
        )
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'tasks', filter: `workspace_id=eq.${workspaceId}` },
          (payload) => {
            if (subscriptionGeneration !== _operatingSubscriptionGeneration) return;
            const r = payload.new as any;
            const updated: Task = {
              id: r.id, title: r.title, ownerId: r.owner_id ?? undefined,
              dueDate: r.due_date ?? undefined, completed: r.completed,
              completedAt: r.completed_at ?? undefined, createdAt: r.created_at ?? undefined,
              issueId: r.issue_id ?? undefined,
              delayCount: r.delay_count ?? 0,
              year: r.year ?? undefined,
              quarter: r.quarter ?? undefined,
              rockId: r.rock_id ?? undefined,
              status: (r.status ?? (r.completed ? 'done' : 'open')) as Task['status'],
              queuedAction: r.queued_action ?? undefined,
            };
            set((s) => ({
              tasks: s.tasks.map((a) => a.id === updated.id ? { ...a, ...updated } : a),
            }));
          }
        )
        .subscribe((status, err) => {
          if (subscriptionGeneration !== _operatingSubscriptionGeneration) return;
          if (status === 'CHANNEL_ERROR') {
            console.error('[operating] realtime channel error — will retry', err);
            supabase.removeChannel(_operatingChannel!);
            _operatingChannel = null;
            setTimeout(setup, 3000);
          } else if (status === 'TIMED_OUT') {
            console.warn('[operating] realtime channel timed out — will retry');
            supabase.removeChannel(_operatingChannel!);
            _operatingChannel = null;
            setTimeout(setup, 3000);
          }
        });
    };

    setup();
  },

  unsubscribeFromRealtime: () => {
    _operatingSubscriptionGeneration += 1;
    if (_operatingChannel) {
      supabase.removeChannel(_operatingChannel);
      _operatingChannel = null;
    }
  },

  nullifyItemCycles: async (workspaceId, inferDueDates) => {
    const cleared = await coreApi(`/workspaces/${workspaceId}/item-cycles/clear`, { infer_due_dates: inferDueDates });
    if (cleared.error) {
      storeNotice.error("Couldn't turn off cycles for this workspace.");
      return;
    }

    if (inferDueDates) {
      set((s) => ({
        rocks: s.rocks.map((r) =>
          r.year != null && r.quarter != null && !r.dueDate
            ? { ...r, dueDate: dateToLocalStr(new Date(r.year!, r.quarter! * 3, 0)) }
            : r,
        ),
      }));
    }

    set((s) => ({
      rocks: s.rocks.map((r) => ({ ...r, year: undefined, quarter: undefined })),
      tasks: s.tasks.map((a) => ({ ...a, year: undefined, quarter: undefined })),
      issues: s.issues.map((i) => ({ ...i, year: undefined, quarter: undefined })),
      availableYears: [],
      availableQuarters: [],
      activeYear: null,
      activeQuarter: null,
      currentYear: null,
      currentQuarter: null,
      cycles: [],
      cycleVisions: [],
      unlockedCycleKey: null,
      unlockedBaselineRockId: null,
      cycleBarOpen: false,
    }));
  },

  assignCyclesToCyclelessItems: async (workspaceId, year, quarter) => {
    const assigned = await coreApi(`/workspaces/${workspaceId}/item-cycles/assign`, { year, quarter });
    if (assigned.error) {
      storeNotice.error("Couldn't move this workspace's work into the cycle.");
      return;
    }

    set((s) => ({
      rocks: s.rocks.map((r) => r.year == null ? { ...r, year, quarter } : r),
      tasks: s.tasks.map((a) => a.year == null ? { ...a, year, quarter } : a),
      issues: s.issues.map((i) => i.year == null ? { ...i, year, quarter } : i),
    }));
  },
}));
